import ContextMenuController from "./context_menu_controller"
import { orderQuadCorners } from "../lib/homography"

// Image annotation tool — extends the base context-menu primitive.
//
// The base owns the overlay panel mechanics (positioning, open/close lifecycle,
// Escape / click-away). This controller adds the annotation-specific behaviour:
//
//   - NO "annotate mode": gallery pan/zoom/rotate is ALWAYS live. Right-click is
//     the single annotate gesture (image menu "Add annotation here"; box menu
//     edits/deletes). Left-click selects a box for edit + (if product-linked)
//     cross-links to the ranging row. Left-drag pans (gallery) UNLESS a one-shot
//     draw is armed: "Add annotation here" arms a single press-drag-release that
//     rubber-bands a box to the dragged size (or, on a bare click, drops a small
//     default box), then disarms so left-drag pans again. The one-shot is the
//     only create path — there is no persistent draw mode.
//   - The overlay (saved-box SVG + draft + resize handles) is pinned to the
//     RENDERED image rect (untransformed object-contain box) AND inherits the
//     gallery's live transform: translate/scale/rotate is re-applied to each
//     overlay layer on every gallery:transform event, with the same
//     transform-origin (centre) as the image. So boxes/handles stay glued to the
//     shelf as the user zooms/pans/rotates, and normalisation stays in the
//     simple untransformed image-rect space.
//   - Screen→image mapping inverts ONLY the gallery transform (subtract centre,
//     un-translate, un-rotate, un-scale) to land back in the untransformed rect,
//     then divides by rect size for 0–1 image coords. So a box dropped while
//     zoomed/panned stores TRUE image coords and is correct again at 1×.
//   - Handles live on the transformed handles layer, so each dot is
//     counter-scaled (1/scale) and counter-rotated (−rotation) to stay a usable,
//     upright on-screen size at any zoom.
//   - The "what is this?" panel captures label + optional linked record + notes,
//     then submits a Turbo Stream form so the saved box + list row render back
//     from the server (no HTML in JS).
//
// Existing annotations are server-rendered as SVG <rect>s in the gallery
// partial; this controller manages the new-box panel + selection/resize/move.
//
// Targets are re-declared so DOM attrs resolve against this controller's
// registered identifier ("image-annotation"). Markup uses
// data-image-annotation-target="...".
export default class extends ContextMenuController {
  static targets = [
    "menu", "frame", "loading", "content", // inherited panel shell
    "surface",   // the draw surface laid over the image
    "svgOverlay",// the SVG that renders saved boxes (sized to the image rect)
    "draft",     // the live (in-progress) rect element
    "polygonOverlay", // SVG hosting the in-progress polygon (Phase B, ADR-0082)
    "polygonGroup",   // <g> the live quad outline + vertex dots are drawn into
    "polygonStatus",  // fixed status pill prompting the next corner (TL→TR→BR→BL)
    "quadField",      // hidden form field carrying the quad JSON string
    "panel",     // the "what is this?" form panel
    "form",      // the create form
    "labelSelect",
    "recordPicker",   // wrapper around the inline record search
    "recordSearch",   // text input
    "recordResults",  // results list
    "annotatableType",
    "annotatableId",
    "selectedRecord", // chip showing the chosen record
    "bboxX", "bboxY", "bboxWidth", "bboxHeight",
    "annotationRow", // a row in the Annotate pane list
    "box",           // a saved SVG box on the image overlay
    "legend",        // the layers/legend panel wrapper (hidden when no boxes)
    "legendRows",    // the <ul> host for one toggle row per present class
    "labels",        // px-space layer hosting a class-coloured label pill per box
    "handles",       // px-space layer hosting resize/move handles for the selection
    "emptyState",    // "no annotations yet" placeholder in the list
    "boxMenu",       // the right-click context menu for a saved box
    "menuOpenImage", // the box menu's "open image in new tab" anchor
    "menuCopyLabel", // the box menu's "copy image URL" label span (for feedback)
    "imageMenu",          // the right-click context menu for the empty image surface
    "imageMenuOpenImage", // the image menu's "open image in new tab" anchor
    "imageMenuCopyLabel", // the image menu's "copy image URL" label span (for feedback)
    "pointToggle",        // the "Point mode" on/off switch (ADR-0144)
    "pointTrack", "pointKnob", "pointState", // its visual parts + "On"/"Off" text
    "pointChip",          // one sticky-label chip per class in the Point mode block
    "pointStatus"         // live "N points on this image" line
  ]

  static values = {
    imageId: Number,
    imageUrl: String, // the full image url, for the menu's open/copy actions
    searchUrl: String,
    // label -> WIN record type map; absent label = label-only (no link)
    recordTypes: { type: Object, default: {} }
  }

  connect() {
    super.connect()
    this.editing = false        // mid-drag on a handle / body
    this.selectedId = null       // currently selected annotation id (edit mode)
    // Per-class visibility: the set of class slugs (e.g. "product", "bay") the
    // user has toggled OFF. Default empty = everything visible. Client-side only;
    // not persisted (card #1689). Boxes + rows of a hidden class get display:none.
    this.hiddenLabels = new Set()
    // Live gallery transform mirror — overlay layers inherit this so boxes and
    // handles track the image through zoom/pan/rotate. Seeded to identity; the
    // gallery emits gallery:transform on connect (initializeFirstImage →
    // applyTransform) and on every interaction.
    this.transform = { scale: 1, originX: 0, originY: 0, rotation: 0, css: "" }
    this.boundEditMove = this.editMove.bind(this)
    this.boundEditEnd = this.editEnd.bind(this)
    this.connectPointMode()

    // Track the gallery's transform so the overlay can re-apply it. Listen on
    // this element (the gallery is a descendant; the event bubbles up here).
    this.boundGalleryTransform = this.onGalleryTransform.bind(this)
    this.element.addEventListener("gallery:transform", this.boundGalleryTransform)

    // Follow gallery image switches (card #1689). The annotation pane is
    // per-image but the gallery swaps the displayed image client-side, so on
    // each switch we re-fetch the now-displayed image's boxes + list from the
    // server and re-point the create-target/search-url at it. Listen on this
    // element (the gallery is a descendant; the event bubbles up here).
    this.boundImageChanged = this.onGalleryImageChanged.bind(this)
    this.element.addEventListener("gallery:image-changed", this.boundImageChanged)

    // Deselect on Escape and on click-away (anywhere outside a box, a handle,
    // a list row, or the capture panel). Document-level so it catches clicks on
    // the rest of the page too.
    this.boundDeselectKey = this.handleDeselectKey.bind(this)
    this.boundClickAway = this.handleClickAway.bind(this)
    document.addEventListener("keydown", this.boundDeselectKey)
    document.addEventListener("click", this.boundClickAway, true)

    // Keep the overlay + draw surface pinned to the RENDERED image rect (the
    // letterboxed object-contain box), not the container. The image loads
    // async (naturalWidth is only known on load) and the rect changes on
    // resize, so sync on both.
    this.boundSyncOverlay = this.syncOverlayToImage.bind(this)
    window.addEventListener("resize", this.boundSyncOverlay)
    const mainImage = this.mainImageElement()
    if (mainImage) {
      mainImage.addEventListener("load", this.boundSyncOverlay)
    }
    // Sync now in case the image is already loaded (cached / Turbo restore).
    requestAnimationFrame(this.boundSyncOverlay)

    // Re-draw the label pills whenever the saved-box SVG group changes (a
    // create/update/destroy Turbo Stream appends/replaces/removes a box <g>).
    // One observer covers all three mutation paths so labels stay in sync
    // without each path remembering to re-render.
    const boxesGroup = this.element.querySelector("#image_annotation_boxes")
    if (boxesGroup && "MutationObserver" in window) {
      this.boxObserver = new MutationObserver((mutations) => {
        // A create/update Turbo Stream that targets an SVG <g> is parsed by
        // Turbo with the HTML parser, so the replacement <g>/<rect> land in the
        // XHTML namespace, not the SVG namespace. An HTML-namespaced <rect>
        // inside an <svg> has NO geometry — it renders at 0×0 with a 1px
        // non-scaling-stroke fallback, i.e. the box VANISHES even though it's in
        // the DOM with correct attrs. This is the disappearing-box bug (#1689).
        // Repair every freshly-added box <g> into the SVG namespace before any
        // redraw so the box renders. The repair re-inserts a node, which would
        // re-fire the observer; the namespace guard makes the second pass a
        // no-op (already SVG), so there is no loop.
        this.repairBoxNamespaces(mutations)
        // Boxes may have changed; reorder by area (big at back, small on top),
        // re-apply class visibility, then redraw labels and (if a selection
        // survived) its handles on the next frame so the DOM has settled.
        //
        // CRITICAL (card #1689): orderBoxesByArea() re-appends every box <g> to
        // this same group, which fires THIS observer again. If we rebuilt the
        // legend on every pass we would destroy + recreate the chip <button>s on
        // a plain visibility toggle (which reorders nothing, but the observer
        // still wakes), wiping the chip the user just tapped so the toggle looked
        // dead. So the legend is rebuilt ONLY when the SET of present classes
        // actually changed — i.e. an annotation was added or removed. A reorder
        // or a display:none write leaves the class set identical and skips the
        // rebuild entirely (refreshLegendIfClassesChanged is a cheap no-op then).
        // hiddenLabels survives across any legitimate rebuild, so toggle state is
        // never lost when classes do change.
        requestAnimationFrame(() => {
          this.orderBoxesByArea()
          this.refreshLegendIfClassesChanged()
          this.applyVisibility()
          this.renderLabels()
          this.updatePointStatus()
          if (this.selectedId && !this.editing) {
            this.markSelection()
            this.renderHandles()
          }
        })
      })
      this.boxObserver.observe(boxesGroup, { childList: true })
    }

    // Signature of the class set the legend was last built for. Lets the
    // observer skip the (DOM-destroying) rebuild when only visibility/order
    // changed. Seeded null so the first build always runs. Card #1689.
    this.legendSignature = null

    // Initial ordering + legend for the server-rendered boxes (the observer only
    // fires on subsequent mutations). Defer a frame so the box <g>s are present.
    requestAnimationFrame(() => {
      this.orderBoxesByArea()
      this.buildLegend()
    })
  }

  // Walk the added nodes from a mutation batch; any box <g> that arrived in the
  // wrong (XHTML) namespace is replaced in place with an SVG-namespaced clone so
  // it actually renders. See the observer comment for why Turbo produces these.
  repairBoxNamespaces(mutations) {
    const SVG_NS = "http://www.w3.org/2000/svg"
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return
        if (node.tagName?.toLowerCase() !== "g") return
        if (node.namespaceURI === SVG_NS) return // already correct — no-op, no loop
        const fixed = this.toSvgNamespace(node, SVG_NS)
        node.replaceWith(fixed)
      })
    })
  }

  // Deep-clone an element tree into the SVG namespace, preserving tag names,
  // attributes (incl. data-* and the inline style) and text. Used to rescue a
  // box <g> Turbo created in the XHTML namespace.
  toSvgNamespace(el, ns) {
    const clone = document.createElementNS(ns, el.tagName.toLowerCase())
    for (const attr of el.attributes) {
      clone.setAttribute(attr.name, attr.value)
    }
    el.childNodes.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        clone.appendChild(this.toSvgNamespace(child, ns))
      } else {
        clone.appendChild(child.cloneNode(true))
      }
    })
    return clone
  }

  disconnect() {
    this.disconnectPointMode()
    this.removeEditListeners()
    this.disarmDraw() // tear down any live one-shot draw listeners
    this.disarmPolygon() // tear down any live polygon-picker listeners (Phase B)
    if (this.boxObserver) this.boxObserver.disconnect()
    if (this.boundMenuClose) this.removeMenuListeners()
    document.removeEventListener("keydown", this.boundDeselectKey)
    document.removeEventListener("click", this.boundClickAway, true)
    this.element.removeEventListener("gallery:transform", this.boundGalleryTransform)
    this.element.removeEventListener("gallery:image-changed", this.boundImageChanged)
    window.removeEventListener("resize", this.boundSyncOverlay)
    const mainImage = this.mainImageElement()
    if (mainImage) {
      mainImage.removeEventListener("load", this.boundSyncOverlay)
    }
    super.disconnect()
  }

  // --- Image-rect anchoring --------------------------------------------

  // The gallery controller owns getRenderedImageRect(); reuse it so draw-time
  // and render-time share one reference rect. Fall back to a local computation
  // if the gallery controller is not resolvable yet.
  galleryController() {
    const galleryEl = this.element.querySelector("[data-controller~='gallery']")
    if (!galleryEl) return null
    return this.application.getControllerForElementAndIdentifier(galleryEl, "gallery")
  }

  mainImageElement() {
    return this.element.querySelector("[data-gallery-target='mainImage']")
  }

  // The gallery container element — the position:relative box the overlay layers
  // are absolutely positioned inside, so its viewport origin converts the
  // container-relative rendered-image rect into viewport coordinates.
  containerElement() {
    const img = this.mainImageElement()
    return (img && img.closest("[data-gallery-target='container']")) || null
  }

  // Returns the rendered image rect in container-relative pixels. Used to PIN the
  // overlay layers (they are absolutely positioned inside the container, so their
  // left/top are container-relative).
  renderedImageRect() {
    const gallery = this.galleryController()
    if (gallery && typeof gallery.getRenderedImageRect === "function") {
      return gallery.getRenderedImageRect()
    }
    return this.computeRenderedImageRect()
  }

  // Returns the rendered image rect in VIEWPORT pixels — the same untransformed
  // object-contain box, but with left/top offset by the container's on-screen
  // origin. screenToNormalised compares against this because event.clientX/Y are
  // viewport coordinates; mixing them with the container-relative rect produced a
  // constant offset equal to the container's viewport origin (the header above
  // the preview gave the ~1-inch downward shift, card #1689). Recomputed live each
  // call (getBoundingClientRect reflects the current scroll/layout), so a scrolled
  // preview maps correctly. One source of truth: derived from renderedImageRect()
  // plus the container origin — no divergent mapping.
  viewportImageRect() {
    const rect = this.renderedImageRect()
    if (!rect) return null
    const container = this.containerElement()
    if (!container) return rect
    const origin = container.getBoundingClientRect()
    return {
      left: rect.left + origin.left,
      top: rect.top + origin.top,
      width: rect.width,
      height: rect.height
    }
  }

  // Local fallback mirroring gallery_controller#getRenderedImageRect.
  computeRenderedImageRect() {
    const img = this.mainImageElement()
    const container = img && img.closest("[data-gallery-target='container']")
    if (!img || !container || !img.naturalWidth || !img.naturalHeight) {
      return null
    }
    const containerRect = container.getBoundingClientRect()
    const imgAspect = img.naturalWidth / img.naturalHeight
    const containerAspect = containerRect.width / containerRect.height
    let width, height, left, top
    if (imgAspect > containerAspect) {
      width = containerRect.width
      height = width / imgAspect
      left = 0
      top = (containerRect.height - height) / 2
    } else {
      height = containerRect.height
      width = height * imgAspect
      top = 0
      left = (containerRect.width - width) / 2
    }
    return { left, top, width, height }
  }

  // Pin the SVG overlay (saved boxes), the draft container, and the handles
  // layer to the UNTRANSFORMED rendered image rect (the object-contain box at
  // scale 1 / origin 0). After this, each layer occupies exactly the same box
  // the image would at rest, so:
  //   • the SVG's 0–1 viewBox (preserveAspectRatio="none") maps onto the image,
  //   • px draft/handle maths is image-rect-relative,
  //   • applying the gallery's transform (translate/scale/rotate) with the same
  //     centre origin makes every layer track the image visually.
  syncOverlayToImage() {
    const rect = this.renderedImageRect()
    if (!rect || !rect.width || !rect.height) return
    const apply = (el) => {
      if (!el) return
      el.classList.remove("inset-0")
      el.style.left = `${rect.left}px`
      el.style.top = `${rect.top}px`
      el.style.width = `${rect.width}px`
      el.style.height = `${rect.height}px`
      // Match the image's transform-origin so a shared transform string lines up.
      el.style.transformOrigin = "center center"
    }
    if (this.hasSurfaceTarget) apply(this.surfaceTarget)
    if (this.hasSvgOverlayTarget) apply(this.svgOverlayTarget)
    if (this.hasLabelsTarget) apply(this.labelsTarget)
    if (this.hasHandlesTarget) apply(this.handlesTarget)
    if (this.hasPolygonOverlayTarget) apply(this.polygonOverlayTarget)
    // Re-apply the live gallery transform to the freshly-pinned layers.
    this.applyOverlayTransform()
    // Labels live in the layer's local px-space; a rect change must redraw them
    // at their new positions (the pills are anchored to each box's top-left).
    this.renderLabels()
    // The handles layer is in the layer's local px-space; a rect change (resize)
    // must redraw the selection's handles at their new positions.
    if (this.selectedId && !this.editing) this.renderHandles()
  }

  // --- Inherit the gallery transform -----------------------------------

  // Cache the gallery transform and re-apply it to the overlay layers so saved
  // boxes, the draft, and the handles track the image as it zooms/pans/rotates.
  onGalleryTransform(event) {
    const d = event.detail || {}
    this.transform = {
      scale: d.scale ?? 1,
      originX: d.originX ?? 0,
      originY: d.originY ?? 0,
      rotation: d.rotation ?? 0,
      css: d.transform || ""
    }
    this.applyOverlayTransform()
    // Labels are counter-scaled/rotated to stay upright + legible, so a
    // zoom/rotate must redraw them at the right on-screen size.
    this.renderLabels()
    // Handles are counter-scaled/rotated to stay usable, so a zoom/rotate must
    // redraw them (unless we're mid-drag, when editMove redraws each frame).
    if (this.selectedId && !this.editing) this.renderHandles()
    // Polygon vertex dots are counter-scaled too, so redraw them while a pick is
    // armed so the dots stay a usable on-screen size through zoom.
    if (this.polygonArmed) this.renderPolygon()
  }

  // --- Follow a gallery image switch (card #1689) ----------------------
  //
  // The gallery swaps the displayed image client-side (thumbnail / next / prev)
  // with no server reload, firing gallery:image-changed with the new image id +
  // url. The annotation pane is per-image, so EVERYTHING annotation-related must
  // re-point at the now-displayed image:
  //
  //   • imageIdValue / imageUrlValue → so a new box, the create form's image_id,
  //     the box/image right-click menus, and persistGeometry all target the
  //     CURRENT image, not the entry image.
  //   • searchUrlValue → the annotatable_search path is per-image; recompute it
  //     so the SKU picker queries against the current image's client scope.
  //   • the overlay boxes (#image_annotation_boxes) + the side list
  //     (#image_annotation_list) → re-fetched server-rendered from the index
  //     endpoint and swapped in via Turbo. The box MutationObserver then repairs
  //     SVG namespaces, rebuilds the legend, and redraws labels — the same path
  //     a create/destroy stream travels, so no parallel rebuild logic is needed.
  //
  // Any active selection / edit / draw / open menu is cleared first so no stale
  // red box, handle, draft, or panel lingers over the new image. The render-once
  // overlay guard (@image_annotation_overlay_rendered) is a SERVER-side flag that
  // ensures the single SVG/list/layers markup is emitted once per request; it is
  // untouched here — we never add a second overlay, we only swap the CONTENTS of
  // the one overlay's boxes group + list, so the single overlay and its observer
  // survive intact across any number of switches.
  onGalleryImageChanged(event) {
    const detail = event.detail || {}
    const newImageId = detail.imageId
    if (!newImageId) return

    // No-op when the "switch" is to the image already shown — this also fires on
    // gallery connect for the entry image (initializeFirstImage → showImage),
    // whose boxes/list are already server-rendered. Re-clicking the active
    // thumbnail is likewise a no-op. Switching away then back DOES differ, so the
    // original set is re-fetched correctly.
    if (String(newImageId) === String(this.imageIdValue)) return

    // Tear down any per-image interaction state before the content swaps so
    // nothing from the previous image bleeds onto the new one.
    this.deselect()        // drop selection + handles + red box
    this.disarmDraw()      // cancel any armed one-shot draw
    this.disarmPolygon()   // cancel any armed polygon pick (Phase B)
    this.clearDraft()
    this.hidePanel()       // close the capture/edit panel + clear pendingBox
    this.closeMenus()      // hide box/image context menus
    // Reset per-class visibility so the new image starts fully visible — the
    // legend chips are rebuilt from the new image's present classes, and a class
    // hidden on the previous image shouldn't silently hide boxes on this one.
    this.hiddenLabels.clear()

    // Re-point the controller values at the now-displayed image. searchUrl is
    // per-image (annotatable_search is nested under the image), so recompute it
    // from the new id rather than reusing the entry image's path.
    this.imageIdValue = newImageId
    if (detail.imageUrl) this.imageUrlValue = detail.imageUrl
    this.searchUrlValue =
      `/images/${newImageId}/image_annotations/annotatable_search`

    this.fetchAnnotationsForCurrentImage()
  }

  // Fetch the current image's boxes + list (server-rendered) and swap them into
  // the two containers via Turbo. The box MutationObserver fires on the swap to
  // repair namespaces / rebuild the legend / redraw labels. After the swap we
  // re-anchor the overlay to the (possibly different aspect-ratio) image and
  // toggle the empty-state placeholder from the new annotation count.
  fetchAnnotationsForCurrentImage() {
    const url = `/images/${this.imageIdValue}/image_annotations`
    fetch(url, { headers: { "Accept": "text/vnd.turbo-stream.html" } })
      .then((r) => r.text())
      .then((html) => {
        window.Turbo.renderStreamMessage(html)
        // Let Turbo apply the stream + the observer settle, then re-sync the
        // overlay to the new image and reconcile the empty-state placeholder.
        requestAnimationFrame(() => {
          this.syncOverlayToImage()
          // Full swap → rebuild the legend authoritatively (the present-class
          // set may be entirely different, incl. empty). buildLegend records the
          // new signature so the observer's incremental check stays consistent.
          this.buildLegend()
          this.renderLabels()
          this.syncEmptyState()
        })
      })
      .catch(() => {})
  }

  // Show the "no annotations yet" placeholder iff the swapped-in list is empty.
  // The placeholder is a sibling of the <ul> (see previews/components/_annotate),
  // hidden by saved() on the first create; on an image switch we drive it from
  // the new image's actual box count so an empty image reads empty and a
  // populated one hides it.
  syncEmptyState() {
    if (!this.hasEmptyStateTarget) return
    const hasBoxes = this.boxTargets.length > 0
    this.emptyStateTarget.classList.toggle("hidden", hasBoxes)
  }

  // Apply the cached gallery transform string to every overlay layer. They are
  // pinned to the untransformed image rect with the same centre origin, so the
  // identical transform glues them to the image.
  applyOverlayTransform() {
    const css = this.transform.css ||
      `translate(${this.transform.originX}px, ${this.transform.originY}px) scale(${this.transform.scale}) rotate(${this.transform.rotation}deg)`
    const apply = (el) => { if (el) el.style.transform = css }
    if (this.hasSurfaceTarget) apply(this.surfaceTarget)
    if (this.hasSvgOverlayTarget) apply(this.svgOverlayTarget)
    if (this.hasLabelsTarget) apply(this.labelsTarget)
    if (this.hasHandlesTarget) apply(this.handlesTarget)
    // The polygon overlay tracks the image too while a pick is in progress.
    if (this.hasPolygonOverlayTarget) apply(this.polygonOverlayTarget)
  }

  // Read the live gallery transform from the controller if resolvable (covers
  // the first paint before any gallery:transform event fires), else the cache.
  currentTransform() {
    const gallery = this.galleryController()
    if (gallery && typeof gallery.transformState === "function") {
      const s = gallery.transformState()
      return { scale: s.scale, originX: s.originX, originY: s.originY, rotation: s.rotation }
    }
    return this.transform
  }

  // Map a viewport (screen) point to NORMALISED 0–1 image coords by inverting
  // the gallery transform. The image is positioned at the rendered rect, then
  // CSS-transformed about its CENTRE by translate→scale→rotate. To invert: take
  // the point relative to the rect centre, undo translate, undo rotate, undo
  // scale → that lands in the untransformed-rect-centre frame; add the centre
  // back, subtract the rect origin, divide by rect size.
  screenToNormalised(clientX, clientY, rectOverride = null) {
    // VIEWPORT rect: clientX/clientY are viewport coords, so the reference rect
    // must be too. Using the container-relative rect here was the constant-offset
    // bug (card #1689). Resize/move appeared correct only because they work on a
    // delta of two screenToNormalised() results, which cancels the shared offset;
    // the draw path uses an absolute mapped point, so the offset showed.
    const rect = rectOverride || this.viewportImageRect()
    if (!rect || !rect.width || !rect.height) return null
    const t = this.currentTransform()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2

    // Point relative to the transformed centre, with translate removed.
    let dx = clientX - cx - t.originX
    let dy = clientY - cy - t.originY

    // Undo rotation (rotate by −θ).
    const rad = (-t.rotation * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const rx = dx * cos - dy * sin
    const ry = dx * sin + dy * cos

    // Undo scale.
    const ux = rx / (t.scale || 1)
    const uy = ry / (t.scale || 1)

    // Back into untransformed-rect coordinates, then normalise to 0–1.
    const imgX = ux + rect.width / 2
    const imgY = uy + rect.height / 2
    return { x: imgX / rect.width, y: imgY / rect.height }
  }

  // --- Draft rect rendering --------------------------------------------
  //
  // The draft is the live rect for the one-shot "Add annotation here" draw. It
  // lives in the draft container (pinned to the image rect and sharing the
  // gallery transform), so it tracks the right spot at any zoom/pan. It shows
  // while dragging the rubber-band and as the bare-click default preview.

  updateDraft() {
    if (!this.hasDraftTarget) return
    const px = this.draftPixels()
    const d = this.draftTarget
    d.style.left = `${px.left}px`
    d.style.top = `${px.top}px`
    d.style.width = `${px.width}px`
    d.style.height = `${px.height}px`
    d.classList.remove("hidden")
  }

  clearDraft() {
    if (this.hasDraftTarget) this.draftTarget.classList.add("hidden")
  }

  draftPixels() {
    return {
      left: Math.min(this.drawStartX, this.drawCurrentX),
      top: Math.min(this.drawStartY, this.drawCurrentY),
      width: Math.abs(this.drawCurrentX - this.drawStartX),
      height: Math.abs(this.drawCurrentY - this.drawStartY)
    }
  }

  // --- "What is this?" panel -------------------------------------------

  populateForm() {
    if (!this.pendingBox) return
    this.bboxXTarget.value = this.pendingBox.x
    this.bboxYTarget.value = this.pendingBox.y
    this.bboxWidthTarget.value = this.pendingBox.width
    this.bboxHeightTarget.value = this.pendingBox.height
    // Phase B (ADR-0082): when the box was authored with the polygon picker, carry
    // the 4-point quad as a JSON string so the server stores it and derives the
    // bbox from it. The rect-drag path leaves pendingQuad null, so the field is
    // cleared and the save is bbox-only — unchanged behaviour, no regression.
    if (this.hasQuadFieldTarget) {
      this.quadFieldTarget.value = this.pendingQuad
        ? JSON.stringify(this.pendingQuad)
        : ""
    }
    this.clearRecord()
    if (this.hasLabelSelectTarget) this.onLabelChange()
  }

  showPanel(x, y) {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("hidden")
    // Reuse the base positioner via the menu target if the panel is the menu;
    // otherwise position the panel near the cursor, viewport-clamped.
    this.positionPanel(x, y)
  }

  hidePanel() {
    if (this.hasPanelTarget) this.panelTarget.classList.add("hidden")
    this.clearDraft()
    this.pendingBox = null
    this.pendingQuad = null
  }

  positionPanel(x, y) {
    const panel = this.panelTarget
    panel.style.visibility = "hidden"
    panel.classList.remove("hidden")
    const r = panel.getBoundingClientRect()
    let left = x
    if (x + r.width > window.innerWidth - 8) left = x - r.width
    left = Math.max(8, left)
    let top = y
    if (y + r.height > window.innerHeight - 8) top = y - r.height
    top = Math.max(8, top)
    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
    panel.style.visibility = "visible"
  }

  // When the class changes, show/hide the inline record picker depending on
  // whether the label maps to a WIN record type.
  onLabelChange() {
    const label = this.labelSelectTarget.value
    const type = this.recordTypesValue[label]
    this.clearRecord()
    if (type) {
      this.annotatableTypeTarget.value = type
      if (this.hasRecordPickerTarget) {
        this.recordPickerTarget.classList.remove("hidden")
        this.recordPickerTarget.dataset.recordType = type
      }
    } else {
      this.annotatableTypeTarget.value = ""
      if (this.hasRecordPickerTarget) this.recordPickerTarget.classList.add("hidden")
    }
  }

  // --- Inline record search (no slim_select inside the overlay) --------

  searchRecords() {
    const query = this.recordSearchTarget.value.trim()
    const type = this.annotatableTypeTarget.value
    if (!type) return

    clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => {
      const url = `${this.searchUrlValue}?type=${encodeURIComponent(type)}&q=${encodeURIComponent(query)}`
      fetch(url, { headers: { "Accept": "application/json" } })
        .then((r) => r.json())
        .then((data) => this.renderResults(data.results || []))
        .catch(() => this.renderResults([]))
    }, 250)
  }

  // Build the small thumbnail tile for a result/chip. When the record carries a
  // real thumbnail URL we render an <img>; otherwise a neutral placeholder tile
  // with a `photo` heroicon — never a broken <img>. `size` is a Tailwind size-*
  // class so the same builder serves the row (size-10) and the chip (size-5).
  recordThumbnail(thumbnailUrl, name, sizeClass, iconSizeClass) {
    const base = `${sizeClass} shrink-0 rounded-md bg-slate-100 ring-1 ring-slate-200`
    if (thumbnailUrl) {
      const img = document.createElement("img")
      img.src = thumbnailUrl
      img.alt = name || ""
      img.loading = "lazy"
      img.className = `${base} object-cover`
      return img
    }

    const tile = document.createElement("span")
    tile.className = `${base} flex items-center justify-center text-slate-300`
    tile.setAttribute("data-placeholder", "true")
    // Heroicons `photo` (outline) — inline SVG, no HTML string templating.
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    svg.setAttribute("viewBox", "0 0 24 24")
    svg.setAttribute("fill", "none")
    svg.setAttribute("stroke", "currentColor")
    svg.setAttribute("stroke-width", "1.5")
    svg.setAttribute("aria-hidden", "true")
    svg.classList.add(...iconSizeClass.split(" "))
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("stroke-linecap", "round")
    path.setAttribute("stroke-linejoin", "round")
    path.setAttribute(
      "d",
      "m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M2.25 8.25h.008v.008H2.25V8.25Zm.75 11.25h18a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3A1.5 1.5 0 0 0 1.5 6v12a1.5 1.5 0 0 0 1.5 1.5Z"
    )
    svg.appendChild(path)
    tile.appendChild(svg)
    return tile
  }

  renderResults(results) {
    if (!this.hasRecordResultsTarget) return
    const list = this.recordResultsTarget
    list.replaceChildren()
    results.forEach((rec) => {
      const li = document.createElement("li")
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className =
        "flex items-center gap-2.5 w-full text-left px-2 py-1.5 rounded-md hover:bg-sapphire/5 focus:bg-sapphire/5 focus:outline-none"
      btn.dataset.recordId = rec.id
      btn.dataset.recordName = rec.name

      btn.appendChild(this.recordThumbnail(rec.thumbnail_url, rec.name, "size-10", "size-5"))

      const text = document.createElement("span")
      text.className = "min-w-0 flex-1"
      const nameLine = document.createElement("span")
      nameLine.className = "block text-sm text-slate-800 truncate"
      nameLine.textContent = rec.name
      text.appendChild(nameLine)
      if (rec.identifier) {
        const idLine = document.createElement("span")
        idLine.className = "block text-xs text-slate-400 truncate"
        idLine.textContent = rec.identifier
        text.appendChild(idLine)
      }
      btn.appendChild(text)

      btn.addEventListener("click", () => this.selectRecord(rec))
      li.appendChild(btn)
      list.appendChild(li)
    })
  }

  // Accepts the full record object so the chip can carry the thumbnail too.
  selectRecord(rec) {
    this.annotatableIdTarget.value = rec.id
    if (this.hasSelectedRecordTarget) {
      const chip = this.selectedRecordTarget
      chip.replaceChildren()
      chip.appendChild(this.recordThumbnail(rec.thumbnail_url, rec.name, "size-5", "size-3"))
      const label = document.createElement("span")
      label.className = "truncate"
      label.textContent = rec.name
      chip.appendChild(label)
      chip.classList.remove("hidden")
    }
    if (this.hasRecordResultsTarget) this.recordResultsTarget.replaceChildren()
    if (this.hasRecordSearchTarget) this.recordSearchTarget.value = ""
  }

  clearRecord() {
    if (this.hasAnnotatableIdTarget) this.annotatableIdTarget.value = ""
    if (this.hasRecordResultsTarget) this.recordResultsTarget.replaceChildren()
    if (this.hasRecordSearchTarget) this.recordSearchTarget.value = ""
    if (this.hasSelectedRecordTarget) {
      this.selectedRecordTarget.textContent = ""
      this.selectedRecordTarget.classList.add("hidden")
    }
  }

  cancel(event) {
    if (event) event.preventDefault()
    this.hidePanel()
  }

  // After the create/edit form submits successfully via Turbo Stream, the server
  // appends/replaces the box + row; tidy up the draft + panel. On a validation
  // failure (422) the server re-renders the form with errors INTO the frame, so
  // we keep the panel open in that case — gate on the submit-end success flag.
  saved(event) {
    if (event && event.detail && event.detail.success === false) return
    this.hidePanel()
    // Once the first box lands, drop the "no annotations yet" placeholder.
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    // A newly drawn crop / a panel re-save changes the crop geometry — tell the
    // gallery so its crop-toggle dataset (cropQuad) refreshes. The Turbo Stream
    // has appended/replaced the box <g> by now; defer one frame so the new <g> is
    // in the DOM when the gallery re-reads it.
    requestAnimationFrame(() => this.signalAnnotationChanged())
  }

  // Tell the gallery in THIS pane that an annotation changed (created, edited,
  // resized, or deleted) so it can refresh the crop-toggle dataset off the live
  // overlay (ADR-0082 Phase C). Dispatched from svgOverlay — a DESCENDANT of the
  // gallery element — so it bubbles UP to the gallery's listener. (The
  // controller's own element is an ANCESTOR of the gallery, so dispatching there
  // would not reach it.) No-op when the overlay isn't present (right pane).
  signalAnnotationChanged() {
    if (!this.hasSvgOverlayTarget) return
    this.svgOverlayTarget.dispatchEvent(
      new CustomEvent("image-annotation:changed", { bubbles: true })
    )
  }

  // --- Box click: select for edit + ranging cross-link ----------------

  // A left-click on a saved box (or its list row) selects it for geometry
  // editing — it shows the red selection box + resize/move handles. The list-row
  // delete button is a separate control, ignored here so its form submit is not
  // cancelled.
  //
  // RANGING NAV DISABLED (card #1689): clicking a product-linked annotation used
  // to also switch the right pane to Ranging, scroll to and flash the matching
  // row. Jay found that jarring ("I lose my spot"), so the auto pane-switch +
  // scroll is OFF by default. The cross-link code path is intact below
  // (highlightRangingRow / showRangingPane / ensureRangingLoaded / waitForRangingRow)
  // but is no longer called from this click path. To re-enable a non-jarring,
  // opt-in version later, call this.highlightRangingRow(el?.dataset?.productId)
  // from the appropriate gesture (e.g. a dedicated "Show in ranging" button).
  selectOrHighlightBox(event) {
    if (event?.target?.closest("button, a, input, [type='submit']")) return

    if (event) {
      event.preventDefault()
      // Stop the document click-away handler (capture phase) from immediately
      // deselecting the box we are selecting.
      event.stopPropagation()
    }
    const el = event?.currentTarget
    const annotationId = el?.dataset?.annotationId
    if (!annotationId) return

    // ADR-0144: in Point mode a click on a (non-point) box must DROP a point on the
    // object inside it, not select the box. Points themselves stay selectable (to
    // delete one). The press already bubbled to the gallery container, where
    // pointMouseUp places the point, so just step out of the way here.
    if (this.pointMode && el.dataset.point !== "true" && !el.matches?.("li")) return

    this.selectAnnotation(annotationId)
    // Ranging cross-link intentionally NOT invoked here — see method comment.
  }

  // --- Hover a list row → highlight its box on the image ---------------
  //
  // Hovering an annotation row in the side list brightens the matching box on
  // the image so the eye can connect list ↔ image. This is hover-only and
  // distinct from click (select-for-edit): it never changes the selection, never
  // shows handles, never persists. mouseleave restores the box.
  //
  // GUARD (card #1689): only acts when the image overlay is actually present in
  // the left pane — i.e. a matching box <g> exists in the DOM. If the gallery for
  // this image isn't shown (no svgOverlay / no matching box), hovering does
  // nothing and never errors.
  hoverRow(event) {
    if (!this.hasSvgOverlayTarget) return // overlay not present in the left pane
    const id = event?.currentTarget?.dataset?.annotationId
    if (!id) return
    this.applyHoverHighlight(id, true)
  }

  unhoverRow(event) {
    if (!this.hasSvgOverlayTarget) return
    const id = event?.currentTarget?.dataset?.annotationId
    if (!id) return
    this.applyHoverHighlight(id, false)
  }

  // --- Reverse cross-link: ranging row → annotation box ----------------
  //
  // The REVERSE of the (disabled) annotation→ranging nav (card #1689). When the
  // user is on the RANGING pane (right) with the gallery showing on the left,
  // interacting with a product row that IS annotated on the CURRENT image drives
  // the gallery directly: hover highlights the product's box(es); click zooms +
  // centres the gallery on the box region. Useful (not jarring) because the user
  // stays in the ranging pane — only the left-pane gallery moves.
  //
  // The image-annotation controller is hoisted to the dual-pane wrapper (an
  // ancestor of BOTH panes), so the ranging rows drive these methods via
  // data-action with no extra plumbing. The product id rides on the row's
  // data-ranging-row-product-id; product-linked boxes carry data-product-id (see
  // _box.html.erb), so we match row → box(es) on the product id.
  //
  // GUARDS (all three methods):
  //   • Skip when the gesture lands on an interactive control in the row (the
  //     I/O/C/X status buttons, save, approve, note, the status/thumbnail links).
  //     Same closest("button,a,input,select,label") guard used for the annotation
  //     list's delete button — so changing ranging status is never intercepted.
  //   • Only act when the gallery overlay is present in the left pane
  //     (hasSvgOverlayTarget) AND at least one box for that product exists on the
  //     current image. No box → clean no-op (no error, no zoom). A box that lives
  //     on a DIFFERENT image than the one shown is also a no-op (we don't switch
  //     images) because productBoxes() only sees boxes rendered for the current
  //     image's overlay.

  // The product id for a ranging-row event, or null when the gesture should be
  // ignored (overlay absent, an interactive control was hit, or no product id).
  rangingProductId(event) {
    if (!this.hasSvgOverlayTarget) return null // gallery not shown in the left pane
    // Let the row's own controls (status buttons, save, links, inputs) keep their
    // behaviour — never hijack a ranging-status change for a highlight/zoom.
    if (event?.target?.closest("button, a, input, select, label")) return null
    const id = event?.currentTarget?.dataset?.rangingRowProductId
    return id || null
  }

  // The set of product-linked box <g>s on the CURRENT image for a product id. A
  // product may be annotated more than once (multiple facings), so this returns
  // all matches. Empty when the product isn't annotated on the shown image.
  productBoxes(productId) {
    if (!productId) return []
    return this.boxTargets.filter(
      (g) => g.dataset.productId === String(productId)
    )
  }

  // Hover a ranging row → brighten the matching product's box(es) on the image,
  // reusing the exact same hover-highlight as the annotation-list hover so the
  // two read identically. Multiple boxes for one product all light up. No-op when
  // the product has no box on the current image (productBoxes empty).
  highlightBoxesForProduct(event) {
    const productId = this.rangingProductId(event)
    if (!productId) return
    this.productBoxes(productId).forEach((g) => {
      this.applyHoverHighlight(g.dataset.annotationId, true)
    })
  }

  // mouseleave → clear the hover highlight on the product's box(es). resetBoxStyle
  // (via applyHoverHighlight off) returns each box to its canonical resting render
  // and skips the SELECTED box, so an active edit selection is never disturbed.
  unhighlightBoxesForProduct(event) {
    const productId = this.rangingProductId(event)
    if (!productId) return
    this.productBoxes(productId).forEach((g) => {
      this.applyHoverHighlight(g.dataset.annotationId, false)
    })
  }

  // Click a ranging row → zoom + centre the gallery on the product's annotation.
  // Single box: frame that box. Multiple boxes: frame their bounding rect so all
  // facings are in view. No-op when the product has no box on the current image.
  zoomToProductAnnotation(event) {
    const productId = this.rangingProductId(event)
    if (!productId) return
    const boxes = this.productBoxes(productId)
    if (boxes.length === 0) return // product not annotated on this image — no-op

    const region = this.boundingNormalisedRect(boxes)
    if (!region) return

    const gallery = this.galleryController()
    if (!gallery || typeof gallery.zoomToRegion !== "function") return
    gallery.zoomToRegion(region)
  }

  // The normalised 0–1 bounding rect enclosing every box <g> in the list (their
  // data-bbox-* attrs). One box → that box's rect; many → the union. Returns null
  // if no box carries valid geometry.
  boundingNormalisedRect(boxes) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    boxes.forEach((g) => {
      const x = parseFloat(g.dataset.bboxX)
      const y = parseFloat(g.dataset.bboxY)
      const w = parseFloat(g.dataset.bboxWidth)
      const h = parseFloat(g.dataset.bboxHeight)
      if ([x, y, w, h].some(Number.isNaN)) return
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x + w)
      maxY = Math.max(maxY, y + h)
    })
    if (minX === Infinity) return null
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
  }

  // Toggle a bright highlight on the box <g> for `id`. Brightens the class fill
  // and adds a glow ring via a drop-shadow filter — clear but not garish. The
  // SELECTED box is left alone (it already reads red with handles) so hover never
  // fights the edit selection. No-op if the box isn't rendered (guarded).
  //
  // mouseleave (on = false) routes through the single resetBoxStyle path so the
  // box always returns to its canonical resting render — class colour + a real
  // 1px stroke + 0.12 fill + no glow — rather than an ad-hoc partial restore.
  // That kills the "box went invisible after a hover raced a select/deselect"
  // class of bug (card #1689): one restore, always correct, never stale.
  applyHoverHighlight(id, on) {
    const g = this.boxTargets.find((b) => b.dataset.annotationId === String(id))
    if (!g) return // box not on the image (overlay absent / different image)
    if (String(id) === this.selectedId) return // don't disturb the red selection
    const rect = this.boxShapeElement(g)
    if (!rect) return
    if (on) {
      const colour = g.dataset.colour || "#475569"
      rect.setAttribute("fill-opacity", "0.3")
      // Keep the hover border a FIXED on-screen width too (card #1689). Slightly
      // thicker than resting (2 vs 1) so hover still reads as a lift.
      rect.setAttribute("vector-effect", "non-scaling-stroke")
      rect.setAttribute("stroke-width", "2")
      // Glow in the class colour so the highlight reads as "this one".
      rect.style.filter = `drop-shadow(0 0 4px ${colour})`
    } else {
      this.resetBoxStyle(g) // canonical resting render — never a partial restore
    }
  }

  // THE single source of truth for a box's resting (deselected, unhovered) render.
  //
  // Every path that returns a box to rest MUST call this rather than poking the
  // rect's attributes ad-hoc — deselect, selection-switch (the previously-selected
  // box), mouseleave, and any post-stream re-render. Driving the resting style
  // from one idempotent method means a box can never be left with a stale or
  // invisible stroke: it always lands on the class colour + a real 1px stroke
  // (read in CSS px via vector-effect="non-scaling-stroke") + 0.12 fill + no glow.
  //
  // This is the fix for the disappearing-box bug (#1689): the bug was a whole
  // CLASS of fragile per-path restores that could each leave the rect in a bad
  // state (lingering hover glow, a non-1px stroke, a stale colour). Collapsing
  // them to one call removes the failure surface entirely.
  resetBoxStyle(g) {
    if (!g) return
    const rect = this.boxShapeElement(g)
    if (!rect) return
    const colour = g.dataset.colour || "#475569"
    rect.setAttribute("stroke", colour)
    rect.setAttribute("fill", colour)
    // FIXED on-screen border width regardless of zoom (card #1689). The overlay
    // inherits the gallery's scale, so a plain SVG stroke would scale with it
    // (2px → 4px at 2×). vector-effect="non-scaling-stroke" makes stroke-width be
    // read in SCREEN px and NOT scale with the transform — exactly what's wanted.
    // We re-assert it here (the single resting-style source of truth) so the
    // effect can never be lost by an attribute rewrite. stroke-width is a REAL px
    // count (2), never a 0–1 fraction — a fractional width rendered invisibly
    // (the historical 0.004 bug).
    rect.setAttribute("vector-effect", "non-scaling-stroke")
    rect.setAttribute("fill-opacity", "0.12")
    rect.style.filter = "" // clear any hover drop-shadow glow
    // The CROP box reads distinctly at rest: a DASHED, slightly thicker stroke so
    // the box that doubles as the crop hint is recognisable among normal boxes. It
    // keeps its own class colour (the distinct indigo from COLOURS["crop"]). A crop
    // is simply an annotation whose label == "crop", carried on the <g> as
    // data-label by _box.html.erb. A normal box gets a solid 1px stroke. (Card #1945)
    if (g.dataset.label === "crop") {
      rect.setAttribute("stroke-width", "2")
      rect.setAttribute("stroke-dasharray", "4 3")
    } else {
      rect.setAttribute("stroke-width", "1") // CSS px (non-scaling-stroke) — constant on screen
      rect.removeAttribute("stroke-dasharray")
    }
    g.dataset.boxState = "resting"
  }

  // The selected (red) render — the other resting-adjacent state. Kept beside
  // resetBoxStyle so the two canonical box renders live together and stay in
  // sync. Selecting sets a 2px red stroke + brighter fill; a data-box-state of
  // "selected" lets tests assert the state without colour-matching.
  selectBoxStyle(g) {
    if (!g) return
    const rect = this.boxShapeElement(g)
    if (!rect) return
    const RED = "#dc2626"
    rect.setAttribute("stroke", RED)
    rect.setAttribute("fill", RED)
    // Re-assert non-scaling-stroke so the red selected border is also a FIXED
    // ~2px on-screen at any zoom (card #1689) — same reasoning as resetBoxStyle.
    rect.setAttribute("vector-effect", "non-scaling-stroke")
    rect.setAttribute("stroke-width", "2") // CSS px (non-scaling-stroke) — constant on screen
    rect.setAttribute("fill-opacity", "0.18")
    rect.style.filter = ""
    g.dataset.boxState = "selected"
  }

  // --- Selection + edit (resize / move) --------------------------------

  // Select an annotation by id: mark its box + row selected, then draw the
  // px-space resize/move handles over it.
  selectAnnotation(annotationId) {
    this.selectedId = String(annotationId)
    this.orderBoxesByArea()   // bring the selected box to front (see method doc)
    this.markSelection()
    this.syncOverlayToImage() // ensure handles layer is anchored before drawing
    this.renderLabels()       // recolour the selected pill red
    this.renderHandles()
  }

  deselect() {
    this.selectedId = null
    this.orderBoxesByArea()   // restore pure size order now nothing is selected
    this.markSelection()
    this.renderLabels()       // restore the de-selected pill to its class colour
    this.clearHandles()
  }

  // Reflect selection state on the boxes and list rows.
  //
  // SELECTED box: a clear RED outline (red-600 #dc2626, ~2px on-screen via
  // non-scaling-stroke so stroke-width is read in CSS px) with a brighter fill,
  // so it is obvious which box is being edited regardless of its class colour.
  // The red box + its handles ride the same inherited transform as the saved
  // boxes, so they track zoom/pan/rotate.
  // UNSELECTED boxes keep their per-class colour (restored from data-colour).
  markSelection() {
    this.boxTargets.forEach((g) => {
      const selected = g.dataset.annotationId === this.selectedId
      // Both branches go through the single canonical renderers so a box is
      // never left in a partial/invisible state (card #1689). The selected box
      // reads red (2px); every other box returns to its resting class colour
      // (1px) — including the box we just switched AWAY from.
      if (selected) {
        this.selectBoxStyle(g)
      } else {
        this.resetBoxStyle(g)
      }
    })
    // Sapphire selection on the matching list row (toggle explicit Tailwind
    // classes rather than arbitrary data-[] variants, which the JIT may not
    // emit for a runtime value).
    this.annotationRowTargets.forEach((row) => {
      const selected = row.dataset.annotationId === this.selectedId
      row.classList.toggle("bg-sapphire/5", selected)
      row.classList.toggle("ring-1", selected)
      row.classList.toggle("ring-inset", selected)
      row.classList.toggle("ring-sapphire", selected)
    })
  }

  selectedBoxElement() {
    return this.boxTargets.find((g) => g.dataset.annotationId === this.selectedId) || null
  }

  // The drawable shape element inside a box <g>: a <rect> for axis-aligned/legacy
  // boxes, a <polygon> for a perspective quad (ADR-0082, _box.html.erb). Both
  // carry the SAME visual attributes (fill/fill-opacity/stroke/stroke-width/
  // vector-effect/stroke-dasharray/style.filter), so the hover + select + reset
  // style paths work on either once they target the right element. Before this,
  // those paths queried "rect" only and silently no-op'd on polygon boxes — the
  // perspective box got no hover lift, no red selection, and no live resize.
  boxShapeElement(g) {
    return g ? g.querySelector("rect, polygon") : null
  }

  // Read the selected box's normalised geometry from its data attributes (the
  // server-rendered source of truth), or from a live override during a drag.
  selectedNormalised() {
    if (this._liveBox) return this._liveBox
    const g = this.selectedBoxElement()
    if (!g) return null
    if (g.dataset.point === "true") return null // ADR-0144: a point has no handles
    return {
      x: parseFloat(g.dataset.bboxX),
      y: parseFloat(g.dataset.bboxY),
      width: parseFloat(g.dataset.bboxWidth),
      height: parseFloat(g.dataset.bboxHeight)
    }
  }

  // The selected box's four quad corners ([[x,y]×4], normalised) when it is a
  // PERSPECTIVE quad — and so renders as a <polygon> and edits by its corner
  // nodes — or null for an axis-aligned/legacy box (edited by its 8 bbox handles).
  // The presence of a rendered <polygon> is the source of truth (it matches
  // _box.html.erb's own quad.present? && !quad_axis_aligned? decision), so the
  // edit affordance always agrees with the displayed shape. Returns the live quad
  // mid-drag. Honouring the polygon nodes is exactly what was missing — the bbox
  // handles could never reshape a quad.
  selectedQuad() {
    if (this._liveQuad) return this._liveQuad
    const g = this.selectedBoxElement()
    if (!g || !g.querySelector("polygon")) return null
    const raw = g.dataset.quad
    if (!raw) return null
    try {
      const pts = JSON.parse(raw)
      if (Array.isArray(pts) && pts.length === 4 &&
          pts.every((p) => Array.isArray(p) && p.length === 2)) {
        return pts.map((p) => [p[0], p[1]])
      }
    } catch (e) { /* fall through to null */ }
    return null
  }

  // Draw the 8 resize handles + an invisible move-body, all in PX space on the
  // handles layer. Why px and not the 0–1 SVG: handles need a fixed on-screen
  // size and a usable hit target. In the 0–1 viewBox a 10px handle would be
  // 10/imageWidth units and, with preserveAspectRatio="none", stretch
  // differently on each axis. Computing px positions from the handles layer
  // rect (which equals the rendered image rect — same reference as draw/render/
  // persist) keeps the maths consistent and the targets clickable.
  renderHandles() {
    if (!this.hasHandlesTarget) return
    const box = this.selectedNormalised()
    if (!box) return this.clearHandles()

    // The handles layer is CSS-transformed (it inherits the gallery transform),
    // so its getBoundingClientRect() is the transformed bounding box — NOT its
    // layout size. Work in the layer's UNTRANSFORMED local px space: offsetWidth/
    // offsetHeight equal the pinned image-rect size, which the CSS transform then
    // maps onto the screen exactly as it maps the image. So handle positions are
    // computed in image-rect px and ride the transform automatically.
    const layerW = this.handlesTarget.offsetWidth
    const layerH = this.handlesTarget.offsetHeight
    const left = box.x * layerW
    const top = box.y * layerH
    const w = box.width * layerW
    const h = box.height * layerH

    const layer = this.handlesTarget
    layer.replaceChildren()
    layer.classList.remove("hidden")
    layer.style.pointerEvents = "none"

    // Move-body: a transparent rect over the box's bounding interior, drag to
    // reposition. Same for rect and quad boxes (a quad's move translates all four
    // corners; the body still spans its bbox).
    const body = document.createElement("div")
    body.style.position = "absolute"
    body.style.left = `${left}px`
    body.style.top = `${top}px`
    body.style.width = `${w}px`
    body.style.height = `${h}px`
    body.style.cursor = "move"
    body.style.pointerEvents = "auto"
    body.dataset.handle = "move"
    body.addEventListener("mousedown", (e) => this.editStart(e, "move"))
    layer.appendChild(body)

    // QUAD box → 4 corner NODE handles at the true quad points (honours the
    // polygon nodes — Jay's report). Dragging a node reshapes the quad. Named
    // q0..q3 by corner index so editMove knows which corner moved.
    const quad = this.selectedQuad()
    if (quad) {
      quad.forEach((p, i) => {
        this._appendHandleDot(layer, `q${i}`, p[0] * layerW, p[1] * layerH, "move")
      })
      return
    }

    // RECT box → 8 handles: 4 corners + 4 edge midpoints.
    const handles = [
      ["nw", 0, 0, "nwse-resize"],
      ["n", 0.5, 0, "ns-resize"],
      ["ne", 1, 0, "nesw-resize"],
      ["e", 1, 0.5, "ew-resize"],
      ["se", 1, 1, "nwse-resize"],
      ["s", 0.5, 1, "ns-resize"],
      ["sw", 0, 1, "nesw-resize"],
      ["w", 0, 0.5, "ew-resize"]
    ]
    handles.forEach(([name, fx, fy, cursor]) => {
      this._appendHandleDot(layer, name, left + fx * w, top + fy * h, cursor)
    })
  }

  // Create one resize/edit dot at a layer-local px point and append it. Counter-
  // transforms the dot so it stays a usable, upright ~12px on-screen size at any
  // zoom: the layer applies scale(s) rotate(θ); we divide the dot's drawn size by
  // s and counter-rotate by −θ about its own centre. (scale guarded against 0.)
  // Shared by the rect (8-handle) and quad (4-node) paths so the affordance is
  // identical.
  _appendHandleDot(layer, name, pxLeft, pxTop, cursor) {
    const size = 12 // intended on-screen px size
    const s = this.transform.scale || 1
    const drawn = size / s
    const counterRotate = -(this.transform.rotation || 0)
    const dot = document.createElement("div")
    dot.dataset.handle = name
    dot.style.position = "absolute"
    // Anchor the dot's CENTRE at the handle point, then counter-rotate about that
    // centre (translate(-50%,-50%) keeps the centre fixed under rotation).
    dot.style.left = `${pxLeft}px`
    dot.style.top = `${pxTop}px`
    dot.style.width = `${drawn}px`
    dot.style.height = `${drawn}px`
    dot.style.transform = `translate(-50%, -50%) rotate(${counterRotate}deg)`
    dot.style.transformOrigin = "center center"
    dot.style.boxSizing = "border-box"
    dot.style.borderRadius = "9999px"
    dot.style.background = "#ffffff"
    // White fill + red ring so the handles pop on a busy shelf photo and match the
    // red selection box. Border counter-scaled so it reads ~2px on screen.
    dot.style.border = `${2 / s}px solid #dc2626` // red-600
    dot.style.boxShadow = "0 1px 3px rgba(0,0,0,0.4)"
    dot.style.cursor = cursor
    dot.style.pointerEvents = "auto"
    dot.addEventListener("mousedown", (e) => this.editStart(e, name))
    layer.appendChild(dot)
  }

  clearHandles() {
    if (!this.hasHandlesTarget) return
    this.handlesTarget.replaceChildren()
    this.handlesTarget.classList.add("hidden")
  }

  // --- Auto-layer boxes by size (big at back, small on top) ------------
  //
  // SVG paint order IS z-order: later siblings paint on top of earlier ones, and
  // hit-testing returns the topmost painted element under the cursor. So to make
  // hover/click prefer the SMALLEST box under the pointer (the product inside a
  // big "Bay" box, not the Bay), we sort the box <g>s by bbox AREA DESCENDING —
  // the largest paints first (back), the smallest last (top/front). Because each
  // box's rect has pointer-events:auto, the small box on top wins the hit. This
  // is the whole fix: no per-box hit-test region tweak is needed once the stacking
  // is correct (verified in the system test + browser_evaluate paint-order check).
  //
  // Ordering happens in the UNTRANSFORMED SVG (the 0–1 viewBox); the gallery
  // transform is applied to the whole overlay layer, so zoom/pan/rotate never
  // disturbs sibling order. Re-run on connect, after every box mutation (the
  // observer), and after a persist — any path that adds/edits a box.
  //
  // SELECTED box → FRONT: while a box is selected we move it to the end (top) so
  // its red outline reads clearly and its (separate, px-space) handles sit over an
  // unobscured box. On deselect the next orderBoxesByArea() restores pure size
  // order. The handles layer is z-50 above the SVG regardless, so handles are
  // always reachable; bringing the selected box to front is purely so the red box
  // itself isn't visually buried under a larger sibling while you edit it.
  boxesGroupElement() {
    return this.element.querySelector("#image_annotation_boxes")
  }

  boxArea(g) {
    const w = parseFloat(g.dataset.bboxWidth)
    const h = parseFloat(g.dataset.bboxHeight)
    if (Number.isNaN(w) || Number.isNaN(h)) return 0
    return w * h
  }

  orderBoxesByArea() {
    const group = this.boxesGroupElement()
    if (!group) return
    const boxes = Array.from(group.children).filter(
      (el) => el.tagName && el.tagName.toLowerCase() === "g"
    )
    if (boxes.length < 2 && !this.selectedId) return

    // Largest area first (painted at the back); smallest last (painted on top).
    // Stable tiebreak on id so equal-area boxes keep a deterministic order.
    boxes.sort((a, b) => {
      const diff = this.boxArea(b) - this.boxArea(a)
      if (diff !== 0) return diff
      return (parseInt(a.dataset.annotationId, 10) || 0) -
             (parseInt(b.dataset.annotationId, 10) || 0)
    })

    // The selected box comes to the very front so its red outline is never buried
    // under a larger sibling while editing (see method comment).
    if (this.selectedId) {
      const idx = boxes.findIndex((g) => g.dataset.annotationId === this.selectedId)
      if (idx !== -1) {
        const [sel] = boxes.splice(idx, 1)
        boxes.push(sel)
      }
    }

    // Re-append in computed order. appendChild moves an existing node (no clone),
    // so this is a pure reorder — listeners + selection styling are preserved.
    boxes.forEach((g) => group.appendChild(g))
  }

  // --- Per-class visibility (bulk show/hide by kind) -------------------
  //
  // Build a toggle CHIP per CLASS currently present on the image (only classes
  // that actually have a box — never empty classes). Each chip is a <button>
  // carrying data-label; tapping it (click->image-annotation#toggleLabelVisibility)
  // flips this.hiddenLabels and re-applies visibility. Pure DOM construction (no
  // HTML in JS). The chip set is rebuilt only when the present-class set changes
  // (annotation added/removed) so a plain visibility toggle never destroys the
  // chip the user just tapped — see refreshLegendIfClassesChanged. The legend
  // wrapper is hidden when no boxes exist at all. Card #1689.
  presentLabels() {
    // Map of slug -> { title, colour }, in a stable (alphabetical) order, derived
    // only from boxes currently on the overlay.
    const map = new Map()
    this.boxTargets.forEach((g) => {
      const slug = g.dataset.label
      if (!slug || map.has(slug)) return
      map.set(slug, {
        title: g.dataset.labelTitle || slug.replace(/_/g, " "),
        colour: g.dataset.colour || "#475569"
      })
    })
    return new Map([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])))
  }

  // A stable signature of the present-class set ("bay|price_ticket|product").
  // presentLabels() is already alphabetically ordered, so identical class sets
  // always yield the same string. Used to decide whether a box mutation actually
  // changed which classes exist (rebuild the chips) or was just a reorder /
  // visibility write (leave the chips alone). Card #1689.
  legendClassSignature() {
    return [...this.presentLabels().keys()].join("|")
  }

  // Called from the box MutationObserver. Rebuild the chip row ONLY when the set
  // of present classes changed — adding the first box of a class adds its chip,
  // deleting the last box of a class removes it. A reorder or a display:none
  // write leaves the signature identical and skips the rebuild, so the chip the
  // user just tapped is never destroyed mid-interaction (the dead-toggle bug,
  // card #1689). hiddenLabels persists across the controller's life, so when a
  // legitimate rebuild does run it re-renders each chip in its current on/off
  // state — toggle state is preserved across add/delete.
  refreshLegendIfClassesChanged() {
    if (this.legendClassSignature() === this.legendSignature) return
    this.buildLegend()
  }

  // Build the compact flex-row of toggle chips — one <button> per present class.
  // Each chip carries data-label and a Stimulus click action so the WHOLE chip is
  // the hit target (no fragile tiny checkbox). Visible classes read solid + full
  // colour; hidden classes read obviously OFF (desaturated, struck through, eye
  // slash). Pure DOM construction — no HTML strings. Records the class signature
  // so the observer can skip needless rebuilds. Card #1689.
  buildLegend() {
    if (!this.hasLegendRowsTarget) return
    const labels = this.presentLabels()
    const host = this.legendRowsTarget
    host.replaceChildren()

    // No boxes → hide the whole legend panel (nothing to toggle).
    if (this.hasLegendTarget) this.legendTarget.classList.toggle("hidden", labels.size === 0)
    this.legendSignature = this.legendClassSignature()
    if (labels.size === 0) return

    labels.forEach(({ title, colour }, slug) => {
      const count = this.boxTargets.filter((g) => g.dataset.label === slug).length

      const chip = document.createElement("button")
      chip.type = "button"
      chip.dataset.legendLabel = slug
      chip.dataset.label = slug
      chip.dataset.action = "click->image-annotation#toggleLabelVisibility"
      chip.title = `Show / hide ${title}`
      // Base chip shell — small, pill, bordered. State classes + the colour dot
      // are set by styleChip so on/off lives in one place.
      chip.className =
        "group inline-flex items-center gap-1.5 rounded-full border pl-1.5 pr-2 py-0.5 " +
        "text-[11px] font-medium leading-none transition-colors focus:outline-none " +
        "focus-visible:ring-2 focus-visible:ring-sapphire/40 cursor-pointer select-none"

      // Colour dot.
      const dot = document.createElement("span")
      dot.dataset.chipDot = "true"
      dot.className = "inline-block size-2 rounded-full shrink-0"
      chip.appendChild(dot)

      // Class name.
      const name = document.createElement("span")
      name.dataset.chipName = "true"
      name.className = "capitalize whitespace-nowrap"
      name.textContent = title
      chip.appendChild(name)

      // Per-class count, faded.
      const countEl = document.createElement("span")
      countEl.dataset.chipCount = "true"
      countEl.className = "tabular-nums text-[10px] opacity-60"
      countEl.textContent = String(count)
      chip.appendChild(countEl)

      this.styleChip(chip, slug, colour)
      host.appendChild(chip)
    })
  }

  // Paint a chip's on/off state. ON (visible): solid class colour fill, white
  // text, full-colour dot. OFF (hidden): desaturated slate, struck-through name,
  // hollow dot — obviously off at a glance. Driven from this.hiddenLabels so the
  // same call serves both the initial build and a live toggle without a rebuild.
  styleChip(chip, slug, colour) {
    const hidden = this.hiddenLabels.has(slug)
    const dot = chip.querySelector("[data-chip-dot]")
    const name = chip.querySelector("[data-chip-name]")
    chip.setAttribute("aria-pressed", String(!hidden))
    chip.dataset.chipState = hidden ? "off" : "on"

    if (hidden) {
      chip.style.backgroundColor = ""
      chip.style.borderColor = ""
      chip.style.color = ""
      chip.classList.add("bg-slate-50", "border-slate-200", "text-slate-400")
      if (dot) {
        dot.style.backgroundColor = "transparent"
        dot.style.boxShadow = `inset 0 0 0 1.5px ${colour}`
        dot.style.opacity = "0.5"
      }
      if (name) name.style.textDecoration = "line-through"
    } else {
      chip.classList.remove("bg-slate-50", "border-slate-200", "text-slate-400")
      // Solid class colour fill + white text so the active state is unmistakable.
      chip.style.backgroundColor = colour
      chip.style.borderColor = colour
      chip.style.color = "#ffffff"
      if (dot) {
        dot.style.backgroundColor = "#ffffff"
        dot.style.boxShadow = ""
        dot.style.opacity = "1"
      }
      if (name) name.style.textDecoration = "none"
    }
  }

  // Flip a class on/off and re-apply. Bound to the chip button's click action, so
  // it receives the DOM event; the class slug rides on the chip's data-label. We
  // restyle THIS chip in place (no rebuild → the chip the user tapped survives)
  // and re-apply visibility to the overlay + list. Card #1689.
  toggleLabelVisibility(event) {
    const chip = event?.currentTarget
    const slug = chip?.dataset?.label
    if (!slug) return

    if (this.hiddenLabels.has(slug)) {
      this.hiddenLabels.delete(slug)
    } else {
      this.hiddenLabels.add(slug)
      // If the currently-selected box belongs to a class we're hiding, drop the
      // selection so its handles/red don't linger over a now-invisible box.
      const sel = this.selectedBoxElement()
      if (sel && sel.dataset.label === slug) this.deselect()
    }

    // Restyle the tapped chip in place — never rebuild the row on a toggle.
    const colour = this.boxTargets.find((g) => g.dataset.label === slug)?.dataset.colour || "#475569"
    this.styleChip(chip, slug, colour)
    this.applyVisibility()
  }

  // Apply this.hiddenLabels to the overlay + the list. Hidden classes get
  // display:none on their box <g>s (the must-have) AND on their list rows (clearer
  // than dimming — the row vanishes alongside its box). Visible classes are
  // restored. Labels/pills follow because renderLabels skips boxes whose <g> is
  // display:none. Re-run after any rebuild so streamed boxes inherit the state.
  applyVisibility() {
    this.boxTargets.forEach((g) => {
      const hidden = this.hiddenLabels.has(g.dataset.label)
      g.style.display = hidden ? "none" : ""
    })
    this.annotationRowTargets.forEach((row) => {
      const hidden = this.hiddenLabels.has(row.dataset.label)
      row.style.display = hidden ? "none" : ""
    })
    // Pills for now-hidden boxes must go too; renderLabels honours display:none.
    this.renderLabels()
  }

  // --- Hide / show the label layer during a drag ----------------------
  //
  // While dragging a box (resize/move) or rubber-banding a new one, the pills do
  // NOT follow the geometry mid-gesture — editMove updates only the rect + handles
  // and the draft has no pill — so a visible pill would sit stranded at the old
  // corner and look broken. Jay's chosen fix (card #1689): hide the whole labels
  // layer for the (momentary) duration of the drag, then re-render on release so
  // each pill snaps to its box's final position.
  //
  // We toggle visibility (NOT display:none): a hidden layer keeps its layout box,
  // so its offsetWidth/offsetHeight stay non-zero and renderLabels() can recompute
  // pill positions on show without early-returning on a collapsed layer. The flag
  // lets renderLabels() (called from transform/observer mid-drag) keep the layer
  // hidden instead of flashing it back visible.
  hideLabelsLayer() {
    if (!this.hasLabelsTarget) return
    this.labelsHidden = true
    this.labelsTarget.style.visibility = "hidden"
  }

  showLabelsLayer() {
    if (!this.hasLabelsTarget) return
    this.labelsHidden = false
    this.labelsTarget.style.visibility = ""
    // Snap the pills to current box geometry now the drag has resolved.
    this.renderLabels()
  }

  // --- Class label pills -----------------------------------------------
  //
  // Draw one small class-coloured pill per saved box, anchored at the box's
  // top-left in the labels layer's local px-space. The layer is pinned to the
  // image rect and shares the gallery transform, so the pills ride the image
  // through zoom/pan/rotate — but we counter-scale/counter-rotate each pill so
  // it stays an upright, legible ~11px on screen at any zoom (same trick as the
  // resize handles). Pure DOM construction — no HTML strings.
  renderLabels() {
    if (!this.hasLabelsTarget) return
    const layer = this.labelsTarget
    const layerW = layer.offsetWidth
    const layerH = layer.offsetHeight
    if (!layerW || !layerH) return

    layer.replaceChildren()

    const s = this.transform.scale || 1
    const counterRotate = -(this.transform.rotation || 0)

    this.boxTargets.forEach((g) => {
      // Skip boxes hidden by a class toggle — no pill for an invisible box.
      if (g.style.display === "none") return
      const x = parseFloat(g.dataset.bboxX)
      const y = parseFloat(g.dataset.bboxY)
      if (Number.isNaN(x) || Number.isNaN(y)) return
      const colour = g.dataset.colour || "#475569"
      const selected = g.dataset.annotationId === this.selectedId
      const text = this.boxLabelText(g)
      const id = g.dataset.annotationId

      // ADR-0144: a point is a dot + a compact label, not a box corner pill.
      if (g.dataset.point === "true") {
        this.appendPointMarker(layer, { g, x, y, layerW, layerH, colour, selected, text, id, s, counterRotate })
        return
      }

      const pill = document.createElement("div")
      pill.dataset.annotationLabel = id
      pill.style.position = "absolute"
      pill.style.left = `${x * layerW}px`
      pill.style.top = `${y * layerH}px`
      // Anchor the pill's bottom-left at the box corner, sitting just above it.
      // Counter-scale + counter-rotate so it stays upright and ~constant size.
      pill.style.transformOrigin = "left bottom"
      pill.style.transform =
        `translateY(-100%) scale(${1 / s}) rotate(${counterRotate}deg)`
      pill.style.whiteSpace = "nowrap"
      pill.style.display = "inline-flex"
      pill.style.alignItems = "center"
      pill.style.gap = "4px"
      pill.style.padding = "1px 5px"
      pill.style.borderRadius = "3px"
      pill.style.fontSize = "11px"
      pill.style.lineHeight = "1.3"
      pill.style.fontWeight = "600"
      pill.style.color = "#ffffff"
      pill.style.boxShadow = "0 1px 2px rgba(0,0,0,0.35)"
      pill.style.pointerEvents = "none"
      // Selected pill goes red to match the red selection box; others class colour.
      pill.style.background = selected ? "#dc2626" : colour

      // Stable, visible identifier (the annotation DB id) so a specific box can be
      // referenced by name ("box #47 vanished") and matches what we'd query. Sits
      // as a faded inset badge ahead of the class name. Card #1689.
      const idBadge = document.createElement("span")
      idBadge.dataset.annotationIdBadge = id
      idBadge.textContent = `#${id}`
      idBadge.style.fontWeight = "700"
      idBadge.style.fontVariantNumeric = "tabular-nums"
      idBadge.style.padding = "0 3px"
      idBadge.style.borderRadius = "2px"
      idBadge.style.background = "rgba(255,255,255,0.22)"
      pill.appendChild(idBadge)

      const labelSpan = document.createElement("span")
      labelSpan.style.textTransform = "capitalize"
      labelSpan.textContent = text
      pill.appendChild(labelSpan)

      layer.appendChild(pill)
    })
  }

  // The display text for a box's label pill, read from the matching list row
  // (which the server renders with the human-friendly label_title), falling back
  // to the box's annotation id.
  boxLabelText(g) {
    const id = g.dataset.annotationId
    const row = this.annotationRowTargets.find((r) => r.dataset.annotationId === id)
    const labelEl = row?.querySelector("p.font-medium")
    return (labelEl?.textContent || "").trim() || "annotation"
  }

  // Begin a resize (handle) or move (body) drag. All edit math runs in
  // NORMALISED 0–1 image space — the pointer is mapped through the gallery
  // transform inverse (screenToNormalised), so resize/move work correctly at any
  // zoom/pan/rotation without touching the transformed layer's screen rect.
  // Read-only query for the gallery pan handler (card #1689): true while the user
  // is mid resize/move drag OR while a one-shot draw is armed/drawing. The gallery
  // bails out of starting a pan when this is true, so an edit/draw gesture is
  // never hijacked into a pan even on frames where this controller's listeners
  // have moved to window/document. A plain selection (no drag) leaves `editing`
  // false, so a click-to-select still allows the gallery's no-move press through
  // — but since the press never crosses the pan threshold, nothing pans anyway.
  isEditOrDrawActive() {
    return !!(this.editing || this.drawArmed || this.drawing)
  }

  editStart(event, handle) {
    if (!this.selectedId) return
    event.preventDefault()
    event.stopPropagation()
    this.editing = true
    this.activeHandle = handle
    // Hide the label pills for the duration of the drag (card #1689). The pill is
    // anchored to the box's top-left and is NOT moved during editMove (only the
    // rect + handles update), so a visible pill would lag stuck at the old corner
    // and look broken. Hidden now, re-shown on editEnd at the box's final corner
    // (renderLabels redraws from current geometry). This fires only on a real
    // handle/body mousedown — the start of a drag — never on a plain select.
    this.hideLabelsLayer()

    const start = this.screenToNormalised(event.clientX, event.clientY)
    this.editStartPointer = start || { x: 0, y: 0 }
    this.editStartBox = { ...this.selectedNormalised() } // normalised snapshot
    // For a quad (polygon) box, also snapshot the four corners — the drag reshapes
    // the quad by its nodes, not the bbox edges. Null for rect/legacy boxes, which
    // keep the bbox-edge path. The handle name (q0..q3 / move) drives which.
    this.editStartQuad = this.selectedQuad()

    window.addEventListener("mousemove", this.boundEditMove)
    window.addEventListener("mouseup", this.boundEditEnd)
  }

  editMove(event) {
    if (!this.editing) return
    const p = this.screenToNormalised(event.clientX, event.clientY)
    if (!p) return
    const dx = p.x - this.editStartPointer.x
    const dy = p.y - this.editStartPointer.y

    if (this.editStartQuad) {
      // Quad box: move a corner node (or translate all on a move-body drag), then
      // derive the bbox from the new quad so the handles + move-body track it.
      this._liveQuad = this.applyQuadEdit(this.editStartQuad, this.activeHandle, dx, dy)
      this._liveBox = this.bboxFromQuad(this._liveQuad)
      this.updateSelectedBoxRect(this._liveBox)
      this.renderHandles()
      return
    }

    this._liveBox = this.applyEdit(this.editStartBox, this.activeHandle, dx, dy)
    this.updateSelectedBoxRect(this._liveBox)
    this.renderHandles()
  }

  // Compute the new NORMALISED box from a normalised drag delta, constrained to
  // the unit square. Move translates and clamps so the whole box stays on the
  // photo. Resize moves one or two edges and clamps each edge to [0, 1] with a
  // minimum size so the box can't invert or vanish.
  applyEdit(start, handle, dx, dy) {
    const min = 0.02 // ~2% of the image, minimum box edge
    let { x: left, y: top, width, height } = start
    let right = left + width
    let bottom = top + height

    if (handle === "move") {
      left = this.clamp(left + dx, 0, 1 - width)
      top = this.clamp(top + dy, 0, 1 - height)
      return { x: left, y: top, width, height }
    }

    if (handle.includes("w")) left = this.clamp(left + dx, 0, right - min)
    if (handle.includes("e")) right = this.clamp(right + dx, left + min, 1)
    if (handle.includes("n")) top = this.clamp(top + dy, 0, bottom - min)
    if (handle.includes("s")) bottom = this.clamp(bottom + dy, top + min, 1)

    return { x: left, y: top, width: right - left, height: bottom - top }
  }

  // Compute the new NORMALISED quad from a drag delta. A corner node (q0..q3)
  // moves just that corner, clamped to the unit square — this reshapes the quad
  // and is what "honour the polygon nodes" means. A move-body drag translates all
  // four corners rigidly, clamping the bbox to the photo so the shape stays whole.
  applyQuadEdit(startQuad, handle, dx, dy) {
    if (handle === "move") {
      const xs = startQuad.map((p) => p[0])
      const ys = startQuad.map((p) => p[1])
      const minX = Math.min(...xs), maxX = Math.max(...xs)
      const minY = Math.min(...ys), maxY = Math.max(...ys)
      const tx = this.clamp(minX + dx, 0, 1 - (maxX - minX)) - minX
      const ty = this.clamp(minY + dy, 0, 1 - (maxY - minY)) - minY
      return startQuad.map(([x, y]) => [x + tx, y + ty])
    }
    const i = parseInt(handle.slice(1), 10)
    if (Number.isNaN(i)) return startQuad.map((p) => [p[0], p[1]])
    return startQuad.map(([x, y], idx) =>
      idx === i ? [this.clamp(x + dx, 0, 1), this.clamp(y + dy, 0, 1)] : [x, y]
    )
  }

  // Live-update the selected SVG shape (0–1 attrs) during a drag, no server hit.
  // Quad boxes update the <polygon>'s points from the live quad; rect boxes update
  // the <rect>'s x/y/width/height. Mid-drag both branches read the live geometry.
  updateSelectedBoxRect(box) {
    const g = this.selectedBoxElement()
    if (!g) return
    const polygon = g.querySelector("polygon")
    if (polygon && this._liveQuad) {
      polygon.setAttribute(
        "points",
        this._liveQuad.map(([x, y]) => `${this.round(x)},${this.round(y)}`).join(" ")
      )
      return
    }
    const rect = g.querySelector("rect")
    if (!rect) return
    rect.setAttribute("x", this.round(box.x))
    rect.setAttribute("y", this.round(box.y))
    rect.setAttribute("width", this.round(box.width))
    rect.setAttribute("height", this.round(box.height))
  }

  editEnd() {
    if (!this.editing) return
    this.editing = false
    this.removeEditListeners()
    // Re-show the labels at the box's final geometry. Done BEFORE the no-drag
    // early return so a handle/body mousedown→mouseup with no movement (a click,
    // not a drag) still puts the pill back — renderLabels redraws from current
    // box positions (card #1689).
    this.showLabelsLayer()
    if (!this._liveBox && !this._liveQuad) return

    const box = this._liveBox
    const quad = this._liveQuad
    this._liveBox = null
    this._liveQuad = null
    this.editStartQuad = null
    this.persistGeometry(this.selectedId, box, quad)
  }

  removeEditListeners() {
    window.removeEventListener("mousemove", this.boundEditMove)
    window.removeEventListener("mouseup", this.boundEditEnd)
  }

  // PATCH the new normalised geometry. Turbo Stream re-renders the box + row
  // (with fresh data-bbox-* / data-quad attrs), which keeps selectedNormalised()
  // and selectedQuad() in sync for the next drag. We re-mark + re-draw handles
  // after the stream lands. When a quad is supplied (a quad-node edit) it is sent
  // too; the server re-derives the bbox from it (image_annotation_geometry_params
  // permits :quad) and re-renders the <polygon> at the new shape. Without it the
  // bbox-only rect path is unchanged.
  persistGeometry(annotationId, box, quad = null) {
    const url = `/images/${this.imageIdValue}/image_annotations/${annotationId}`
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new FormData()
    body.append("_method", "patch")
    body.append("image_annotation[bbox_x]", this.round(box.x))
    body.append("image_annotation[bbox_y]", this.round(box.y))
    body.append("image_annotation[bbox_width]", this.round(box.width))
    body.append("image_annotation[bbox_height]", this.round(box.height))
    if (quad) {
      body.append(
        "image_annotation[quad]",
        JSON.stringify(quad.map(([x, y]) => [this.round(x), this.round(y)]))
      )
    }

    fetch(url, {
      method: "POST",
      headers: { "Accept": "text/vnd.turbo-stream.html", "X-CSRF-Token": token },
      body
    })
      .then((r) => r.text())
      .then((html) => {
        window.Turbo.renderStreamMessage(html)
        // The box element was replaced; re-mark selection and redraw handles
        // against the persisted geometry.
        requestAnimationFrame(() => {
          this.markSelection()
          this.renderLabels()
          this.renderHandles()
          // A quad-node resize of the CROP box changes what the gallery toggle
          // straightens to — refresh its crop dataset off the re-rendered box.
          this.signalAnnotationChanged()
        })
      })
      .catch(() => {})
  }

  handleDeselectKey(event) {
    if (event.key === "Escape" && this.selectedId) this.deselect()
  }

  // Deselect when the click lands outside any box/handle/list row/panel.
  handleClickAway(event) {
    if (!this.selectedId) return
    const t = event.target
    if (
      t.closest("[data-image-annotation-target='box']") ||
      t.closest("[data-image-annotation-target='handles']") ||
      t.closest("[data-image-annotation-target='annotationRow']") ||
      t.closest("[data-image-annotation-target='panel']")
    ) {
      return
    }
    this.deselect()
  }

  // --- Cross-pane link: annotation ⇄ ranging row -----------------------

  // Switch the right pane to ranging, scroll the product's row into view, and
  // apply a transient highlight. Called only when NOT in annotate mode (see
  // selectOrHighlightBox). The ranging row owns its own I/O/C/X status control.
  highlightRangingRow(productId) {
    if (!productId) return

    // Only cross-link when a Ranging pane is actually present (staff + the
    // response has a client + place). Otherwise leave the panes untouched.
    const hasRangingPane = this.element.querySelector(
      "[data-content-type='ranging_review']"
    )
    if (!hasRangingPane) return

    // Surface the Ranging pane so the highlighted row is visible. The
    // pane-selector controller shares this controller's element.
    this.showRangingPane()

    // The ranging table is lazy-loaded behind a "Load Ranging Data" button, so
    // the row may not exist yet. Trigger the load, then poll briefly for the row.
    this.ensureRangingLoaded()
    this.waitForRangingRow(productId, 0)
  }

  showRangingPane() {
    const selector = this.application.getControllerForElementAndIdentifier(
      this.element, "pane-selector"
    )
    if (selector && typeof selector.setRightPane === "function") {
      selector.setRightPane("ranging_review")
    }
  }

  // Click the ranging pane's load button if the table has not been fetched yet.
  ensureRangingLoaded() {
    const anyRow = this.element.querySelector("[data-ranging-row-product-id]")
    if (anyRow) return
    const loadButton = this.element.querySelector(
      "[data-ranging-review-target='loadButton']"
    )
    if (loadButton && !loadButton.disabled) loadButton.click()
  }

  // Poll for the row up to ~3s while the lazy ranging table loads.
  waitForRangingRow(productId, attempt) {
    const row = this.findRangingRow(productId)
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" })
      this.flashRow(row)
      return
    }
    if (attempt >= 20) return
    setTimeout(() => this.waitForRangingRow(productId, attempt + 1), 150)
  }

  findRangingRow(productId) {
    return this.element.querySelector(
      `[data-ranging-row-product-id="${CSS.escape(productId)}"]`
    )
  }

  flashRow(row) {
    const highlight = ["ring-2", "ring-sapphire", "bg-sapphire/5", "rounded-md"]
    row.classList.add(...highlight)
    clearTimeout(this._flashTimer)
    this._flashTimer = setTimeout(() => {
      row.classList.remove(...highlight)
    }, 2200)
  }

  // --- Right-click context menu on a saved box / list row --------------
  //
  // Reuses the base context_menu primitive's lifecycle: the base owns
  // viewport-aware positioning (positionMenu, here reused via positionBoxMenu
  // mirroring the same maths against the boxMenu target) and the Escape /
  // click-away / scroll close listeners (addListeners / removeListeners /
  // handleKeydown / preventScroll). We do NOT build a parallel menu system — we
  // only swap the target the lifecycle acts on, and override close() to hide the
  // boxMenu (the base's close hides menuTarget, which this controller does not
  // declare; its hasMenuTarget guard means the base call is a safe no-op).
  //
  // The menu content is server-rendered (image_annotations/_context_menu) — no
  // HTML is built in JS. Unlike the album menu it fetches nothing (the items are
  // client actions on the already-loaded annotation), so there is no
  // loading/turbo-frame swap; openBoxMenu just stashes which box was clicked,
  // points the "open image" link at the image url, positions, and shows.
  openBoxMenu(event) {
    if (!this.hasBoxMenuTarget) return
    // Only intercept when the Annotate pane is the active right pane. On
    // Response/Ranging/etc., let the right-click fall through to the gallery
    // container's album/browser-actions menu (mirrors openImageMenu's gate).
    if (!this.isAnnotatePaneActive()) return
    // Ignore right-clicks on interactive controls inside a row (e.g. the inline
    // delete button) — let those keep their own behaviour.
    if (event.target.closest("button, a, input, [type='submit']")) return

    const el = event.currentTarget
    const annotationId = el?.dataset?.annotationId
    if (!annotationId) return

    event.preventDefault()
    event.stopPropagation()

    this.menuAnnotationId = String(annotationId)

    // Point the "Open image in new tab" link at the full image url.
    if (this.hasMenuOpenImageTarget && this.hasImageUrlValue) {
      this.menuOpenImageTarget.href = this.imageUrlValue
    }
    // Reset any prior "Copied!" feedback on the copy item.
    if (this.hasMenuCopyLabelTarget) this.menuCopyLabelTarget.textContent = "Copy image URL"

    // Close any open image menu first so only one menu is ever visible.
    if (this.hasImageMenuTarget) this.imageMenuTarget.classList.add("hidden")

    this.positionFixedMenu(this.boxMenuTarget, event.clientX, event.clientY)
    this.boxMenuTarget.classList.remove("hidden")

    // Defer wiring the close listeners so the opening contextmenu/click does not
    // immediately re-close the menu (matches the base primitive's pattern).
    requestAnimationFrame(() => this.addMenuListeners())
  }

  // --- Right-click context menu on the EMPTY image surface -------------
  //
  // Bound (in markup) on the gallery CONTAINER, which is always interactive. The
  // overlay layers (svg/draft/handles) are pointer-events-none, so a right-click
  // on empty image area falls through to the container. A right-click ON a saved
  // box is handled by the box <g>'s own contextmenu->openBoxMenu, which
  // stopPropagation()s before it can bubble to the container — clean box-vs-empty
  // separation, no double menu.
  //
  // "Add annotation here" maps the cursor through the transform inverse and drops
  // a default box at the true image location, so it works while zoomed/panned.
  // Content is server-rendered (image_menu) — no HTML in JS; this only positions,
  // stamps the open/copy actions, and shows.
  openImageMenu(event) {
    if (!this.hasImageMenuTarget) return
    // Only intercept right-click when the Annotate pane is the active right pane.
    // When Response, Ranging, etc. is showing, let the event pass through so the
    // native browser context menu (or album menu) behaves normally.
    if (!this.isAnnotatePaneActive()) return
    // Defensive: never open the image menu over a saved box (the box handler
    // already ran + stopped propagation, but a stray bubble shouldn't double up).
    if (event.target.closest("[data-image-annotation-target='box']")) return
    // Let interactive controls (gallery buttons, links) keep their own behaviour.
    if (event.target.closest("button, a, input, [type='submit']")) return

    event.preventDefault()
    event.stopImmediatePropagation()

    // Stash the cursor's viewport point so "Add annotation here" can drop the
    // starter box exactly where the user right-clicked.
    this.imageMenuPoint = { x: event.clientX, y: event.clientY }

    // Point the "Open image in new tab" link at the full image url.
    if (this.hasImageMenuOpenImageTarget && this.hasImageUrlValue) {
      this.imageMenuOpenImageTarget.href = this.imageUrlValue
    }
    // Reset any prior "Copied!" feedback on the copy item.
    if (this.hasImageMenuCopyLabelTarget) this.imageMenuCopyLabelTarget.textContent = "Copy image URL"

    // Close the box menu first so only one menu is ever visible.
    if (this.hasBoxMenuTarget) this.boxMenuTarget.classList.add("hidden")

    this.positionFixedMenu(this.imageMenuTarget, event.clientX, event.clientY)
    this.imageMenuTarget.classList.remove("hidden")

    requestAnimationFrame(() => this.addMenuListeners())
  }

  // Mirror of the base positionMenu maths, against an arbitrary fixed menu
  // element — shared by the box menu and the image menu.
  positionFixedMenu(menu, x, y) {
    menu.style.visibility = "hidden"
    menu.classList.remove("hidden")
    const r = menu.getBoundingClientRect()
    let left = x
    if (x + r.width > window.innerWidth - 8) left = x - r.width
    left = Math.max(8, left)
    let top = y
    if (y + r.height > window.innerHeight - 8) top = y - r.height
    top = Math.max(8, top)
    menu.style.left = `${left}px`
    menu.style.top = `${top}px`
    menu.style.visibility = "visible"
  }

  // Shared close lifecycle for BOTH the box menu and the image menu. Both menus
  // reuse one set of document/scroll listeners so opening either wires the same
  // Escape / click-away / contextmenu / scroll close behaviour; closeMenus()
  // hides whichever is open and tears the listeners down.
  addMenuListeners() {
    if (this.boundMenuClose) return // listeners already active for an open menu
    this.boundMenuClose = this.closeMenus.bind(this)
    this.boundMenuKeydown = this.handleMenuKeydown.bind(this)
    document.addEventListener("click", this.boundMenuClose)
    document.addEventListener("contextmenu", this.boundMenuClose)
    document.addEventListener("keydown", this.boundMenuKeydown)
    window.addEventListener("scroll", this.boundMenuClose, true)
  }

  removeMenuListeners() {
    if (!this.boundMenuClose) return
    document.removeEventListener("click", this.boundMenuClose)
    document.removeEventListener("contextmenu", this.boundMenuClose)
    document.removeEventListener("keydown", this.boundMenuKeydown)
    window.removeEventListener("scroll", this.boundMenuClose, true)
    this.boundMenuClose = null
    this.boundMenuKeydown = null
  }

  handleMenuKeydown(event) {
    if (event.key === "Escape") this.closeMenus()
  }

  // Hide both context menus and remove the shared listeners. Named distinctly
  // from the base close() so item data-actions can call `close` (the base) too —
  // both end up here because close() below delegates to closeMenus().
  closeMenus() {
    if (this.justDragged) return
    if (this.hasBoxMenuTarget) this.boxMenuTarget.classList.add("hidden")
    if (this.hasImageMenuTarget) this.imageMenuTarget.classList.add("hidden")
    this.menuDragEnd()
    this.removeMenuListeners()
  }

  // --- Drag support for annotation context menus ----------------------------

  menuDragStart(event) {
    event.preventDefault()
    event.stopPropagation()
    // Find the menu panel: the dragged header's parent container.
    const menu = event.currentTarget.closest(
      "[data-image-annotation-target='boxMenu'], [data-image-annotation-target='imageMenu']"
    )
    if (!menu) return
    const rect = menu.getBoundingClientRect()
    this.dragMenu    = menu
    this.menuDragOffsetX = event.clientX - rect.left
    this.menuDragOffsetY = event.clientY - rect.top
    this.boundMenuDragMove = this.menuDragMove.bind(this)
    this.boundMenuDragEnd  = this.menuDragEnd.bind(this)
    document.addEventListener("mousemove", this.boundMenuDragMove)
    document.addEventListener("mouseup",   this.boundMenuDragEnd)
  }

  menuDragMove(event) {
    if (!this.dragMenu) return
    const left = Math.max(0, Math.min(event.clientX - this.menuDragOffsetX, window.innerWidth  - 60))
    const top  = Math.max(0, Math.min(event.clientY - this.menuDragOffsetY, window.innerHeight - 60))
    this.dragMenu.style.left = `${left}px`
    this.dragMenu.style.top  = `${top}px`
  }

  menuDragEnd() {
    this.dragMenu = null
    if (this.boundMenuDragMove) {
      document.removeEventListener("mousemove", this.boundMenuDragMove)
      document.removeEventListener("mouseup",   this.boundMenuDragEnd)
      this.boundMenuDragMove = null
      this.boundMenuDragEnd  = null
    }
    this.justDragged = true
    setTimeout(() => { this.justDragged = false }, 100)
  }

  // Override the base close so menu items wired to `image-annotation#close`
  // (the "Open image" links) and any base-triggered close also hide the menus.
  close() {
    super.close() // safe no-op here (no menuTarget declared)
    this.closeMenus()
  }

  // --- Menu item actions ------------------------------------------------

  // "Edit shape": select the box and show the resize/move handles. No mode to
  // enable anymore — selection works whenever a box exists.
  menuEditShape() {
    if (!this.menuAnnotationId) return this.closeMenus()
    this.selectAnnotation(this.menuAnnotationId)
    this.closeMenus()
  }

  // "Add annotation here": the image menu's primary create gesture. Rather than
  // dropping a fixed (and far-too-big) 20% box the user must then resize, this
  // ARMS a ONE-SHOT draw gesture (card #1689): the surface becomes interactive
  // with a crosshair cursor, and the NEXT press-drag-release on the image draws
  // the box to the dragged size (rubber-band from press to release), then opens
  // the "what is this?" panel to label it. After that one box (or Escape) the
  // gesture disarms and left-drag goes back to panning. A bare click (a press
  // with no meaningful drag) drops a much SMALLER default box centred on the
  // click, so a quick click still creates a usable box without forcing a drag.
  //
  // This is a deliberate one-shot, not the old persistent draw mode — it does
  // not conflict with pan because the surface only captures pointer events while
  // armed, then reverts to pointer-events-none on release/escape.
  menuAddAnnotation() {
    // ADR-0144: while Point mode is on, "add" means a point, never a box.
    if (this.pointMode) return this.menuAddPoint()
    this.closeMenus()
    if (!this.hasSurfaceTarget) return

    // Anchor the overlay to the current rendered rect + transform (no zoom reset
    // — navigation stays live), and clear any prior selection/draft.
    this.syncOverlayToImage()
    this.deselect()
    this.clearDraft()

    this.armDraw()
  }

  // Bare-click default box size (fraction of the image per axis). Much smaller
  // than the retired 20% starter so a quick click yields a usable small box.
  static SMALL_FRACTION = 0.05
  // Press-to-release distance (CSS px) below which we treat the gesture as a
  // bare click (drop the small default) rather than a drag (rubber-band).
  static DRAW_CLICK_THRESHOLD = 6

  // Arm the one-shot draw: make the surface capture pointer events with a
  // crosshair cursor and wire a single pointerdown. Everything tears down on
  // release or Escape (disarmDraw), so panning resumes and no listener leaks.
  armDraw() {
    if (this.drawArmed) return
    this.drawArmed = true
    const surface = this.surfaceTarget
    surface.classList.remove("pointer-events-none")
    surface.classList.add("pointer-events-auto")
    surface.style.cursor = "crosshair"

    this.boundDrawStart = this.drawStart.bind(this)
    this.boundDrawMove = this.drawMove.bind(this)
    this.boundDrawEnd = this.drawEnd.bind(this)
    this.boundDrawKey = this.handleDrawKey.bind(this)
    surface.addEventListener("pointerdown", this.boundDrawStart)
    document.addEventListener("keydown", this.boundDrawKey)
  }

  // Revert the surface to its resting, non-capturing state and remove every
  // draw listener. Called after one box is drawn AND on Escape, so an
  // armed-then-Escape leaves everything back to normal (surface non-capturing,
  // no crosshair, no stray draft). Idempotent.
  disarmDraw() {
    if (!this.drawArmed) return
    this.drawArmed = false
    this.drawing = false
    const surface = this.surfaceTarget
    surface.classList.add("pointer-events-none")
    surface.classList.remove("pointer-events-auto")
    surface.style.cursor = ""
    if (this.boundDrawStart) surface.removeEventListener("pointerdown", this.boundDrawStart)
    window.removeEventListener("pointermove", this.boundDrawMove)
    window.removeEventListener("pointerup", this.boundDrawEnd)
    if (this.boundDrawKey) document.removeEventListener("keydown", this.boundDrawKey)
    this.boundDrawStart = null
    this.boundDrawMove = null
    this.boundDrawEnd = null
    this.boundDrawKey = null
  }

  // Pointer press on the armed surface: record the start point (both screen px
  // for the click-vs-drag threshold and normalised image coords for the box),
  // then track move/up on the window so a drag that leaves the surface still
  // resolves. Only the primary button arms a draw.
  drawStart(event) {
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    this.drawing = true
    // Hide existing label pills while rubber-banding a new box (card #1689) so no
    // pill sits stranded over the draft. Re-shown on drawEnd; the new box's own
    // pill appears after the create form saves (the box observer re-renders).
    this.hideLabelsLayer()
    this.drawDownScreen = { x: event.clientX, y: event.clientY }
    this.drawStartNorm = this.screenToNormalised(event.clientX, event.clientY)
    // Seed the draft px endpoints (local surface px) so the live rect can render
    // from the first move.
    const start = this.normalisedToSurfacePixels(this.drawStartNorm)
    this.drawStartX = start.x
    this.drawStartY = start.y
    this.drawCurrentX = start.x
    this.drawCurrentY = start.y
    window.addEventListener("pointermove", this.boundDrawMove)
    window.addEventListener("pointerup", this.boundDrawEnd)
  }

  // Live rubber-band: map the current pointer to surface px and redraw the draft
  // rect, clamped within the image rect.
  drawMove(event) {
    if (!this.drawing) return
    const norm = this.screenToNormalised(event.clientX, event.clientY)
    if (!norm) return
    const clamped = { x: this.clamp(norm.x, 0, 1), y: this.clamp(norm.y, 0, 1) }
    const px = this.normalisedToSurfacePixels(clamped)
    this.drawCurrentX = px.x
    this.drawCurrentY = px.y
    this.updateDraft()
  }

  // Release: decide click vs drag by the screen-space travel. A meaningful drag
  // builds the box to the dragged size (rubber-band from press to release); a
  // bare click drops a small default box centred on the click. Either way we
  // open the panel to label it, then disarm (one-shot).
  drawEnd(event) {
    if (!this.drawing) return
    this.drawing = false
    window.removeEventListener("pointermove", this.boundDrawMove)
    window.removeEventListener("pointerup", this.boundDrawEnd)
    // Restore the existing pills now the rubber-band has resolved (card #1689).
    // The newly-drawn box's pill arrives later, after the create form saves.
    this.showLabelsLayer()

    const down = this.drawDownScreen || { x: event.clientX, y: event.clientY }
    const travel = Math.hypot(event.clientX - down.x, event.clientY - down.y)
    const startNorm = this.drawStartNorm
    const endNorm = this.screenToNormalised(event.clientX, event.clientY)

    let box
    if (travel >= this.constructor.DRAW_CLICK_THRESHOLD && startNorm && endNorm) {
      // Drag: rubber-band from press to release, clamped to the unit square.
      const x1 = this.clamp(Math.min(startNorm.x, endNorm.x), 0, 1)
      const y1 = this.clamp(Math.min(startNorm.y, endNorm.y), 0, 1)
      const x2 = this.clamp(Math.max(startNorm.x, endNorm.x), 0, 1)
      const y2 = this.clamp(Math.max(startNorm.y, endNorm.y), 0, 1)
      box = { x: x1, y: y1, width: x2 - x1, height: y2 - y1 }
    } else {
      // Bare click: a much smaller default box centred on the press point.
      box = this.smallDefaultBox(startNorm || { x: 0.5, y: 0.5 })
    }

    this.openPanelForBox(box, event.clientX, event.clientY)
    this.disarmDraw()
  }

  // Escape while armed cancels the one-shot cleanly: no draft, surface
  // non-capturing, listeners gone.
  handleDrawKey(event) {
    if (event.key !== "Escape") return
    this.clearDraft()
    this.pendingBox = null
    // Escape mid-draw must restore the pills too (card #1689) — they were hidden
    // on drawStart and there's no drawEnd to re-show them on this cancel path.
    this.showLabelsLayer()
    this.disarmDraw()
  }

  // A small default box (SMALL_FRACTION per axis) centred on a normalised point,
  // clamped so the whole box stays inside the unit square.
  smallDefaultBox(centre) {
    const f = this.constructor.SMALL_FRACTION
    return {
      x: this.clamp(centre.x - f / 2, 0, 1 - f),
      y: this.clamp(centre.y - f / 2, 0, 1 - f),
      width: f,
      height: f
    }
  }

  // Stash the pending box (rounded), render its draft rect, and open the label
  // panel near the release point. Shared by the drag and bare-click paths.
  openPanelForBox(box, anchorX, anchorY) {
    // Guard against a zero/degenerate box (e.g. a click that mapped to nothing).
    const min = 0.01
    const w = Math.max(box.width, min)
    const h = Math.max(box.height, min)
    // The rect-drag / bare-click create path is bbox-only — clear any quad so the
    // save is axis-aligned (no regression). The polygon path sets pendingQuad
    // itself before opening the panel via openPanelForQuad.
    this.pendingQuad = null
    this.pendingBox = {
      x: this.round(this.clamp(box.x, 0, 1 - w)),
      y: this.round(this.clamp(box.y, 0, 1 - h)),
      width: this.round(w),
      height: this.round(h)
    }
    this.renderDraftFromNormalised(this.pendingBox)
    this.populateForm()
    this.showPanel(
      anchorX ?? window.innerWidth / 2,
      anchorY ?? window.innerHeight / 2
    )
  }

  // Map a normalised 0–1 image point to the surface layer's LOCAL (untransformed)
  // px space — offsetWidth/offsetHeight are the pinned image-rect size; the
  // shared overlay transform then maps it onto the zoomed/panned image. Mirror of
  // renderDraftFromNormalised's per-corner maths.
  normalisedToSurfacePixels(norm) {
    if (!norm || !this.hasSurfaceTarget) return { x: 0, y: 0 }
    return {
      x: norm.x * this.surfaceTarget.offsetWidth,
      y: norm.y * this.surfaceTarget.offsetHeight
    }
  }

  // Draw the draft rect from a normalised box, in the draft container's local
  // (untransformed) px space — offsetWidth/offsetHeight are the pinned rect size;
  // the shared overlay transform maps the draft onto the zoomed/panned image.
  renderDraftFromNormalised(box) {
    if (!this.hasSurfaceTarget) return
    const w = this.surfaceTarget.offsetWidth
    const h = this.surfaceTarget.offsetHeight
    this.drawStartX = box.x * w
    this.drawStartY = box.y * h
    this.drawCurrentX = (box.x + box.width) * w
    this.drawCurrentY = (box.y + box.height) * h
    this.updateDraft()
  }

  // --- Polygon picker (Phase B, ADR-0082) ------------------------------
  //
  // An ALTERNATIVE create gesture to the rect rubber-band above: place four
  // corners (TL → TR → BR → BL) to define a tight quad, instead of an
  // axis-aligned box. It mirrors the rect one-shot's lifecycle exactly — armed
  // from the image menu, captures the surface, disarms on completion / Escape —
  // so the two never fight and the rect path is completely untouched.
  //
  // The author clicks four points; each placed vertex is draggable for fine
  // adjustment. On the fourth click the quad is complete: the bbox is derived
  // locally (its axis-aligned bounding rect) for the draft preview, the quad is
  // stashed as pendingQuad, and the SAME "what is this?" panel opens. The form's
  // hidden quad field then carries the quad (populateForm); the server (Phase A)
  // re-derives the bbox authoritatively on save. NO homography / matrix3d render
  // here — straightening the image is Phase C.
  //
  // Corner labels in TL→TR→BR→BL order, matching the storage contract and the
  // article #177 prototype's placement order.
  static POLYGON_CORNERS = ["TL", "TR", "BR", "BL"]

  // Arm the polygon picker from the image menu. Mirrors menuAddAnnotation: anchor
  // the overlay, clear any prior selection/draft, then arm.
  menuAddPolygon() {
    this.closeMenus()
    if (!this.hasSurfaceTarget) return
    this.syncOverlayToImage()
    this.deselect()
    this.clearDraft()
    this.armPolygon()
  }

  // Make the surface capture clicks with a crosshair and begin collecting corners.
  // Tears down via disarmPolygon on completion or Escape, so panning resumes and
  // no listener leaks. Idempotent; never co-armed with the rect draw (each menu
  // item disarms the other through deselect/clearDraft + its own arm guard).
  armPolygon() {
    if (this.polygonArmed) return
    // A rect draw must not be live at the same time.
    this.disarmDraw()
    this.polygonArmed = true
    this.polygonPoints = [] // normalised {x,y} corners, in click order
    this.polygonDragIndex = null

    const surface = this.surfaceTarget
    surface.classList.remove("pointer-events-none")
    surface.classList.add("pointer-events-auto")
    surface.style.cursor = "crosshair"

    this.boundPolygonClick = this.polygonClick.bind(this)
    this.boundPolygonKey = this.handlePolygonKey.bind(this)
    surface.addEventListener("pointerdown", this.boundPolygonClick)
    document.addEventListener("keydown", this.boundPolygonKey)

    if (this.hasPolygonOverlayTarget) this.polygonOverlayTarget.classList.remove("hidden")
    this.updatePolygonStatus()
    this.renderPolygon()
  }

  // Revert the surface and remove every polygon listener. Idempotent. Clears the
  // overlay + status pill. Called on completion, on Escape, and on image switch.
  disarmPolygon() {
    if (!this.polygonArmed) return
    this.polygonArmed = false
    this.polygonDragIndex = null
    this.polygonPoints = [] // fully clear placement state so any re-arm starts fresh
    const surface = this.surfaceTarget
    surface.classList.add("pointer-events-none")
    surface.classList.remove("pointer-events-auto")
    surface.style.cursor = ""
    if (this.boundPolygonClick) surface.removeEventListener("pointerdown", this.boundPolygonClick)
    window.removeEventListener("pointermove", this.boundPolygonDragMove)
    window.removeEventListener("pointerup", this.boundPolygonDragEnd)
    if (this.boundPolygonKey) document.removeEventListener("keydown", this.boundPolygonKey)
    this.boundPolygonClick = null
    this.boundPolygonKey = null
    this.boundPolygonDragMove = null
    this.boundPolygonDragEnd = null
    if (this.hasPolygonOverlayTarget) {
      this.polygonOverlayTarget.classList.add("hidden")
    }
    if (this.hasPolygonGroupTarget) this.polygonGroupTarget.replaceChildren()
    this.hidePolygonStatus()
  }

  // A press on the armed surface. If it lands on an already-placed vertex dot,
  // start dragging that vertex (fine adjustment). Otherwise place the next corner;
  // the fourth corner completes the quad. Only the primary button acts.
  polygonClick(event) {
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()

    const hitIndex = this.polygonVertexUnder(event)
    if (hitIndex !== null) {
      this.beginVertexDrag(hitIndex)
      return
    }

    if (this.polygonPoints.length >= 4) return // already have four; drag to adjust

    const norm = this.screenToNormalised(event.clientX, event.clientY)
    if (!norm) return
    this.polygonPoints.push({
      x: this.clamp(norm.x, 0, 1),
      y: this.clamp(norm.y, 0, 1)
    })
    this.renderPolygon()
    this.updatePolygonStatus()

    if (this.polygonPoints.length === 4) this.completePolygon(event.clientX, event.clientY)
  }

  // The index of a placed vertex dot under the pointer (within a small px radius),
  // or null. Used so a click on an existing corner drags it rather than adding a
  // fifth point.
  polygonVertexUnder(event) {
    if (!this.polygonPoints || this.polygonPoints.length === 0) return null
    const rect = this.viewportImageRect()
    if (!rect) return null
    const t = this.currentTransform()
    const HIT = 14 // px radius around a vertex that counts as a grab
    for (let i = 0; i < this.polygonPoints.length; i++) {
      const screen = this.normalisedToScreen(this.polygonPoints[i], rect, t)
      if (!screen) continue
      if (Math.hypot(event.clientX - screen.x, event.clientY - screen.y) <= HIT) return i
    }
    return null
  }

  // Map a normalised 0–1 image point to a VIEWPORT screen point — the forward of
  // screenToNormalised (apply the gallery transform: scale, rotate, translate,
  // about the rect centre). Used for vertex hit-testing.
  normalisedToScreen(norm, rect, t) {
    if (!norm || !rect) return null
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    // Untransformed point relative to the rect centre.
    let ux = norm.x * rect.width - rect.width / 2
    let uy = norm.y * rect.height - rect.height / 2
    // Apply scale.
    ux *= t.scale || 1
    uy *= t.scale || 1
    // Apply rotation (+θ).
    const rad = (t.rotation * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const rx = ux * cos - uy * sin
    const ry = ux * sin + uy * cos
    // Apply translate, back to viewport coords.
    return { x: cx + rx + t.originX, y: cy + ry + t.originY }
  }

  // Begin dragging an existing vertex. Tracks move/up on the window so a drag that
  // leaves the surface still resolves.
  beginVertexDrag(index) {
    this.polygonDragIndex = index
    this.boundPolygonDragMove = this.polygonDragMove.bind(this)
    this.boundPolygonDragEnd = this.polygonDragEnd.bind(this)
    window.addEventListener("pointermove", this.boundPolygonDragMove)
    window.addEventListener("pointerup", this.boundPolygonDragEnd)
  }

  polygonDragMove(event) {
    if (this.polygonDragIndex === null) return
    const norm = this.screenToNormalised(event.clientX, event.clientY)
    if (!norm) return
    this.polygonPoints[this.polygonDragIndex] = {
      x: this.clamp(norm.x, 0, 1),
      y: this.clamp(norm.y, 0, 1)
    }
    this.renderPolygon()
    // If all four are placed, keep the pending quad/draft in sync as it's adjusted.
    if (this.polygonPoints.length === 4 && this.pendingQuad) this.syncPendingQuad()
  }

  polygonDragEnd() {
    if (this.polygonDragIndex === null) return
    this.polygonDragIndex = null
    window.removeEventListener("pointermove", this.boundPolygonDragMove)
    window.removeEventListener("pointerup", this.boundPolygonDragEnd)
  }

  // Four corners placed: capture the quad, then FULLY disarm the picker before
  // opening the panel. Disarming (not a partial manual revert) is what makes the
  // picker re-armable: it resets polygonArmed=false, removes BOTH the click and
  // keydown listeners, clears the points/overlay, and reverts the surface — so a
  // subsequent "Add polygon" passes armPolygon's `if (polygonArmed) return` guard
  // and re-enters placement cleanly. disarmPolygon deliberately does NOT touch
  // pendingQuad, so the captured quad survives into the panel + form save. (Before
  // this, completePolygon left polygonArmed=true and the second arm was dead.)
  completePolygon(anchorX, anchorY) {
    this.syncPendingQuad()
    const quad = this.pendingQuad
    this.disarmPolygon()
    this.pendingQuad = quad // re-assert (disarmPolygon leaves it, but be explicit)

    const box = this.bboxFromQuad(this.pendingQuad)
    this.openPanelForQuad(box, this.pendingQuad, anchorX, anchorY)
  }

  // Recompute pendingQuad (rounded) from the current four points, normalised to
  // canonical TL→TR→BR→BL order via the SHARED orderQuadCorners (single source of
  // truth with the render path). The picker records CLICK order verbatim — a user
  // who clicks down the left side first would otherwise store TL,BL,BR,TR, which
  // reflects/rotates the straighten and self-intersects the overlay polygon.
  // Ordering on capture keeps persisted data clean. The model validates shape +
  // range; we round to keep the payload tidy and match the bbox rounding.
  syncPendingQuad() {
    if (!this.polygonPoints || this.polygonPoints.length !== 4) return
    const rounded = this.polygonPoints.map((p) => [this.round(p.x), this.round(p.y)])
    this.pendingQuad = orderQuadCorners(rounded)
  }

  // The axis-aligned bounding rect of a quad — the SAME derivation the model does
  // server-side (min x/y, span to max). Used only for the local draft preview;
  // the server is authoritative on save.
  bboxFromQuad(quad) {
    const xs = quad.map((p) => p[0])
    const ys = quad.map((p) => p[1])
    const minX = Math.min(...xs)
    const minY = Math.min(...ys)
    return {
      x: minX,
      y: minY,
      width: Math.max(...xs) - minX,
      height: Math.max(...ys) - minY
    }
  }

  // Open the shared "what is this?" panel for a completed quad: stash pendingBox
  // (the derived bbox) + pendingQuad, render the bbox draft, and reveal the panel.
  // Mirrors openPanelForBox but keeps the quad (which openPanelForBox would clear).
  openPanelForQuad(box, quad, anchorX, anchorY) {
    const min = 0.01
    const w = Math.max(box.width, min)
    const h = Math.max(box.height, min)
    this.pendingBox = {
      x: this.round(this.clamp(box.x, 0, 1 - w)),
      y: this.round(this.clamp(box.y, 0, 1 - h)),
      width: this.round(w),
      height: this.round(h)
    }
    this.pendingQuad = quad
    this.renderDraftFromNormalised(this.pendingBox)
    this.populateForm()
    this.showPanel(
      anchorX ?? window.innerWidth / 2,
      anchorY ?? window.innerHeight / 2
    )
  }

  // Draw the live quad into the polygon overlay's 0–1 viewBox group: an SVG
  // <polygon> through the placed points plus a numbered vertex dot per corner.
  // The dots are counter-scaled so they stay a usable on-screen size at any zoom
  // (same reasoning as the resize handles). Re-rendered on every placement/drag.
  renderPolygon() {
    if (!this.hasPolygonGroupTarget) return
    const SVG_NS = "http://www.w3.org/2000/svg"
    const group = this.polygonGroupTarget
    group.replaceChildren()
    const points = this.polygonPoints || []
    if (points.length === 0) return

    // The outline: a closed polygon once 3+ points exist, else a polyline trail.
    const ptsAttr = points.map((p) => `${p.x},${p.y}`).join(" ")
    const shape = document.createElementNS(SVG_NS, points.length >= 3 ? "polygon" : "polyline")
    shape.setAttribute("points", ptsAttr)
    shape.setAttribute("fill", points.length >= 3 ? "#4f46e5" : "none")
    shape.setAttribute("fill-opacity", "0.12")
    shape.setAttribute("stroke", "#4f46e5") // indigo, matching the crop accent
    shape.setAttribute("stroke-width", "2")
    shape.setAttribute("vector-effect", "non-scaling-stroke")
    group.appendChild(shape)

    // Vertex dots, in 0–1 space. Radius is counter-scaled so it reads ~6px on
    // screen regardless of zoom; circles in a non-uniform 0–1 viewBox would
    // otherwise stretch, so we draw small squares (rects) sized in fractional
    // units derived from the layer px size — consistent on both axes.
    const w = this.hasSurfaceTarget ? this.surfaceTarget.offsetWidth || 1 : 1
    const h = this.hasSurfaceTarget ? this.surfaceTarget.offsetHeight || 1 : 1
    const s = this.transform.scale || 1
    const sizePx = 10 / s
    const fx = sizePx / w
    const fy = sizePx / h
    points.forEach((p) => {
      const dot = document.createElementNS(SVG_NS, "rect")
      dot.setAttribute("x", p.x - fx / 2)
      dot.setAttribute("y", p.y - fy / 2)
      dot.setAttribute("width", fx)
      dot.setAttribute("height", fy)
      dot.setAttribute("fill", "#ffffff")
      dot.setAttribute("stroke", "#4f46e5")
      dot.setAttribute("stroke-width", "2")
      dot.setAttribute("vector-effect", "non-scaling-stroke")
      group.appendChild(dot)
    })
  }

  // Escape cancels the polygon pick cleanly: drop points, clear overlay + status,
  // revert the surface.
  handlePolygonKey(event) {
    if (event.key !== "Escape") return
    this.polygonPoints = []
    this.pendingQuad = null
    this.disarmPolygon()
  }

  // Status pill: prompt the next corner, or a "drag to adjust" hint once all four
  // are placed. Fixed, centred at the top of the viewport.
  updatePolygonStatus() {
    if (!this.hasPolygonStatusTarget) return
    const n = (this.polygonPoints || []).length
    const corners = this.constructor.POLYGON_CORNERS
    const text = n < 4
      ? `Click corner ${n + 1} of 4 — ${corners[n]}`
      : "Drag the corners to adjust, then label it"
    this.polygonStatusTarget.textContent = text
    this.polygonStatusTarget.classList.remove("hidden")
  }

  hidePolygonStatus() {
    if (this.hasPolygonStatusTarget) this.polygonStatusTarget.classList.add("hidden")
  }

  // "Edit details": open the panel pre-filled with this annotation's label,
  // linked record (chip + thumbnail) and notes. The form is fetched into its
  // turbo frame from the server (edit action) so the pre-fill is server-rendered
  // (no HTML in JS); we then reveal the panel near the click point.
  menuEditDetails() {
    if (!this.menuAnnotationId) return this.closeMenus()
    const id = this.menuAnnotationId
    this.closeMenus()

    // Cancel any in-progress draw so a stale draft can't submit as a create.
    this.clearDraft()
    this.pendingBox = null

    // Point the form frame at the edit endpoint; Turbo swaps in the pre-filled
    // PATCH form. Show the panel once the frame has loaded.
    const frame = this.element.querySelector("turbo-frame#image_annotation_form")
    if (!frame) return
    frame.addEventListener(
      "turbo:frame-load",
      () => {
        const { x, y } = this.boxAnchorPoint(id)
        this.showPanel(x, y)
      },
      { once: true }
    )
    frame.src = `/images/${this.imageIdValue}/image_annotations/${id}/edit`
  }

  // "Delete": submit the existing destroy via the row's delete button so the
  // server-rendered turbo_stream removes the box + row (one delete path, no
  // duplication).
  menuDelete() {
    if (!this.menuAnnotationId) return this.closeMenus()
    const row = this.annotationRowTargets.find(
      (r) => r.dataset.annotationId === this.menuAnnotationId
    )
    const button = row?.querySelector("button[title='Remove annotation']")
    this.closeMenus()
    if (button) button.click()
  }

  // "Copy image URL": copy the full image url to the clipboard with brief inline
  // feedback. Falls back silently if the clipboard API is unavailable.
  menuCopyImageUrl() {
    const url = this.imageUrlValue
    if (!url) return this.closeMenus()
    const done = () => {
      if (this.hasMenuCopyLabelTarget) this.menuCopyLabelTarget.textContent = "Copied!"
      setTimeout(() => this.closeMenus(), 600)
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => this.closeMenus())
    } else {
      this.closeMenus()
    }
  }

  // The viewport point to anchor the edit panel at: the centre of the box if we
  // can resolve it, else the viewport centre. Keeps the panel near the box the
  // user right-clicked.
  boxAnchorPoint(annotationId) {
    const g = this.boxTargets.find((b) => b.dataset.annotationId === String(annotationId))
    const rect = g?.getBoundingClientRect()
    if (rect && rect.width) {
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
    }
    return { x: window.innerWidth / 2, y: window.innerHeight / 2 }
  }

  // --- Point mode (ADR-0144) ---------------------------------------------
  //
  // A point is the fast annotation: click once on the centre of an object, move on.
  // Point mode is a PERSISTENT toggle (button, or the P key) — unlike the one-shot
  // box draw — because the whole value is rapid repeated clicking across many
  // images. While on:
  //   • a plain left CLICK (press/release with <6px travel) on the image drops a
  //     point with the sticky label, POSTed straight to the existing create
  //     endpoint — no panel, no extra click. A drag still pans (the gallery owns
  //     it) and the wheel still zooms; modifier-clicks are ignored.
  //   • ←/→ (or , / .) step to the previous/next image, wrapping, via the gallery.
  //   • 1–9, 0 pick the sticky label; Z (or Ctrl+Z) undoes the last point on this
  //     image; click a point to select it and Delete/Backspace removes it.
  //   • Esc (with nothing selected) or P again leaves the mode.
  // The sticky label survives image changes and page loads (localStorage).

  // "Add point here": drop a point (sticky label) exactly where the right-click
  // happened. No panel, no arming.
  menuAddPoint() {
    const at = this.imageMenuPoint
    this.closeMenus()
    if (!at) return
    this.syncOverlayToImage()
    const norm = this.pointNormalised(at.x, at.y)
    if (!norm || norm.x < 0 || norm.x > 1 || norm.y < 0 || norm.y > 1) {
      return this.flashPointStatus("That spot is outside the image")
    }
    this.createPoint(norm)
  }

  static POINT_LABEL_KEY = "win.imageAnnotation.pointLabel"
  static POINT_CURSOR_CLASS = "[&_img]:cursor-crosshair!"
  static POINT_CHIP_ON = ["ring-2", "ring-sapphire", "bg-sapphire/10", "text-sapphire", "font-semibold"]
  static POINT_CHIP_OFF = ["ring-1", "ring-slate-200", "bg-white", "text-slate-600"]
static POINT_TRACK_ON = ["bg-sapphire"]
  static POINT_TRACK_OFF = ["bg-slate-200"]

  connectPointMode() {
    this.pointMode = false
    this.pointUndo = new Map() // imageId -> [annotationId, ...] created this session
    this.pointDown = null
    this.pointLabel = this.storedPointLabel()
    this.boundPointKey = this.handlePointKey.bind(this)
    this.boundPointMouseDown = this.pointMouseDown.bind(this)
    this.boundPointMouseUp = this.pointMouseUp.bind(this)
    // capture phase so Esc is seen BEFORE the deselect handler clears the selection
    document.addEventListener("keydown", this.boundPointKey, true)
    requestAnimationFrame(() => this.syncPointChips())
  }

  disconnectPointMode() {
    document.removeEventListener("keydown", this.boundPointKey, true)
    this.detachPointListeners()
  }

  storedPointLabel() {
    let stored = null
    try { stored = window.localStorage.getItem(this.constructor.POINT_LABEL_KEY) } catch (e) { /* private mode */ }
    return stored || "product"
  }

  togglePointMode(event) {
    if (event) event.preventDefault()
    this.pointMode ? this.disablePointMode() : this.enablePointMode()
  }

  enablePointMode() {
    if (this.pointMode) return
    const container = this.containerElement()
    if (!container) return
    this.pointMode = true
    // One gesture at a time: drop any armed box draw / polygon pick / selection.
    this.disarmDraw()
    this.disarmPolygon()
    this.deselect()
    this.hidePanel()
    this.closeMenus()
    container.addEventListener("mousedown", this.boundPointMouseDown)
    container.addEventListener("mouseup", this.boundPointMouseUp)
    container.classList.add(this.constructor.POINT_CURSOR_CLASS)
    this.syncPointToggle()
    this.syncPointChips()
    this.updatePointStatus()
  }

  disablePointMode() {
    if (!this.pointMode) return
    this.pointMode = false
    this.detachPointListeners()
    this.syncPointToggle()
    this.updatePointStatus()
  }

  detachPointListeners() {
    const container = this.containerElement()
    if (!container) return
    container.removeEventListener("mousedown", this.boundPointMouseDown)
    container.removeEventListener("mouseup", this.boundPointMouseUp)
    container.classList.remove(this.constructor.POINT_CURSOR_CLASS)
  }

  syncPointToggle() {
    if (!this.hasPointToggleTarget) return
    this.pointToggleTarget.setAttribute("aria-checked", String(this.pointMode))
    if (this.hasPointTrackTarget) {
      this.pointTrackTarget.classList.remove(...this.constructor.POINT_TRACK_ON, ...this.constructor.POINT_TRACK_OFF)
      this.pointTrackTarget.classList.add(...(this.pointMode ? this.constructor.POINT_TRACK_ON : this.constructor.POINT_TRACK_OFF))
    }
    if (this.hasPointKnobTarget) this.pointKnobTarget.classList.toggle("translate-x-4", this.pointMode)
    if (this.hasPointStateTarget) {
      this.pointStateTarget.textContent = this.pointMode ? "On" : "Off"
      this.pointStateTarget.classList.toggle("text-sapphire", this.pointMode)
      this.pointStateTarget.classList.toggle("text-slate-400", !this.pointMode)
    }
  }

  // Chip click: make this label the sticky one.
  pickPointLabel(event) {
    const label = event.currentTarget.dataset.label
    if (label) this.setPointLabel(label)
  }

  setPointLabel(label) {
    this.pointLabel = label
    try { window.localStorage.setItem(this.constructor.POINT_LABEL_KEY, label) } catch (e) { /* private mode */ }
    this.syncPointChips()
    this.updatePointStatus()
  }

  syncPointChips() {
    if (!this.hasPointChipTarget) return
    // A stored label that is no longer a chip (renamed/removed class) falls back to
    // the first chip so the status line and the highlighted chip always agree.
    if (!this.pointChipTargets.some((c) => c.dataset.label === this.pointLabel)) {
      this.pointLabel = this.pointChipTargets[0].dataset.label
    }
    this.pointChipTargets.forEach((chip) => {
      const on = chip.dataset.label === this.pointLabel
      chip.classList.remove(...this.constructor.POINT_CHIP_ON, ...this.constructor.POINT_CHIP_OFF)
      chip.classList.add(...(on ? this.constructor.POINT_CHIP_ON : this.constructor.POINT_CHIP_OFF))
      chip.setAttribute("aria-pressed", String(on))
    })
  }

  updatePointStatus() {
    if (!this.hasPointStatusTarget) return
    if (!this.pointMode) {
      this.pointStatusTarget.textContent = ""
      return
    }
    const count = this.boxTargets.filter((g) => g.dataset.point === "true").length
    const noun = count === 1 ? "point" : "points"
    const label = this.pointLabel.replace(/_/g, " ")
    this.pointStatusTarget.textContent =
      `Placing points: ${label}. Click the image to drop a point. Done (Esc). ${count} ${noun} on this image.`
  }

  pointMouseDown(event) {
    this.pointDown = event.button === 0
      ? { x: event.clientX, y: event.clientY, target: event.target }
      : null
  }

  pointMouseUp(event) {
    const down = this.pointDown
    this.pointDown = null
    if (!down || event.button !== 0) return
    if (event.altKey || event.shiftKey || event.ctrlKey || event.metaKey) return
    if (!this.isAnnotatePaneActive()) return
    if (Math.hypot(event.clientX - down.x, event.clientY - down.y) >= this.constructor.DRAW_CLICK_THRESHOLD) return
    if (down.target?.closest?.("button, a, input, select, textarea, [data-annotation-marker]")) return

    const norm = this.pointNormalised(event.clientX, event.clientY)
    if (!norm || norm.x < 0 || norm.x > 1 || norm.y < 0 || norm.y > 1) return // letterbox
    this.createPoint(norm)
  }

  // Click -> normalised image coords for point placement. Uses the real rendered
  // image rect; when the image has no natural size (not loaded / failed, so that
  // rect is NaN) it maps over the whole gallery container instead, the same box the
  // px-space layers (labels/markers) are sized to, so a click still lands a point
  // exactly where the marker is drawn.
  pointNormalised(clientX, clientY) {
    const real = this.screenToNormalised(clientX, clientY)
    if (real) return real
    const container = this.containerElement()
    if (!container) return null
    const r = container.getBoundingClientRect()
    return this.screenToNormalised(clientX, clientY, {
      left: r.left, top: r.top, width: r.width, height: r.height
    })
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  // POST a point (zero-size bbox at the click) straight to the create endpoint and
  // feed the Turbo Stream reply to Turbo, exactly as the image-switch fetch does.
  // fetch rather than a form submit so rapid clicks never abort each other (Turbo
  // cancels an in-flight submission when the same form is submitted again).
  createPoint(norm) {
    const imageId = String(this.imageIdValue)
    const pending = this.showPointPending(norm)
    const body = new FormData()
    body.append("image_annotation[label]", this.pointLabel)
    body.append("image_annotation[bbox_x]", this.round(norm.x))
    body.append("image_annotation[bbox_y]", this.round(norm.y))
    body.append("image_annotation[bbox_width]", 0)
    body.append("image_annotation[bbox_height]", 0)
    body.append("image_annotation[recording_mode]", "manual")

    fetch(`/images/${imageId}/image_annotations`, {
      method: "POST",
      body,
      credentials: "same-origin",
      headers: { "Accept": "text/vnd.turbo-stream.html", "X-CSRF-Token": this.csrfToken() }
    })
      .then((response) => response.text().then((html) => ({
        ok: response.ok, id: response.headers.get("X-Image-Annotation-Id"), html
      })))
      .then(({ ok, id, html }) => {
        this.clearPointPending(pending)
        if (!ok) return this.flashPointStatus("Could not save that point")
        this.flashPointStatus(`Added ${this.pointLabel.replace(/_/g, " ")} point`)
        if (id) {
          const stack = this.pointUndo.get(imageId) || []
          stack.push(id)
          this.pointUndo.set(imageId, stack)
        }
        // Only paint it if we are still on that image; otherwise the next image
        // fetch picks it up from the server.
        if (imageId === String(this.imageIdValue)) {
          window.Turbo.renderStreamMessage(html)
          if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
          requestAnimationFrame(() => this.signalAnnotationChanged())
        }
      })
      .catch(() => {
        this.clearPointPending(pending)
        this.flashPointStatus("Could not save that point")
      })
  }

  // Instant feedback while the server round-trips: a one-shot expanding ring at the
  // click plus a translucent placeholder dot in the chosen label's colour, both on
  // the labels layer so they ride zoom/pan/rotate. Removed when the response lands
  // (success or failure); the real point then arrives via the Turbo Stream.
  showPointPending(norm) {
    if (!this.hasLabelsTarget) return []
    const layer = this.labelsTarget
    const layerW = layer.offsetWidth
    const layerH = layer.offsetHeight
    if (!layerW || !layerH) return []
    const s = this.transform.scale || 1
    const counterRotate = -(this.transform.rotation || 0)
    const colour = this.pointColour(this.pointLabel)

    const dot = this.pointDotElement({ x: norm.x, y: norm.y, fill: colour, layerW, layerH, s, counterRotate })
    dot.dataset.pointPending = "true"
    dot.style.opacity = "0.6"
    dot.style.pointerEvents = "none"
    layer.appendChild(dot)

    const echo = document.createElement("div")
    echo.dataset.pointEcho = "true"
    echo.style.position = "absolute"
    echo.style.left = `${norm.x * layerW}px`
    echo.style.top = `${norm.y * layerH}px`
    echo.style.transform = `translate(-50%, -50%) scale(${1 / s}) rotate(${counterRotate}deg)`
    echo.style.pointerEvents = "none"
    const ring = document.createElement("div")
    ring.className = "size-6 rounded-full border-2 animate-ping [animation-iteration-count:1]"
    ring.style.borderColor = colour
    ring.addEventListener("animationend", () => echo.remove())
    echo.appendChild(ring)
    layer.appendChild(echo)
    return [dot, echo]
  }

  clearPointPending(nodes) {
    (nodes || []).forEach((node) => node.remove())
  }

  pointColour(label) {
    const chip = this.pointChipTargets.find((c) => c.dataset.label === label)
    return chip?.dataset.colour || "#475569"
  }

  // Undo the last point added to the displayed image this session.
  undoPoint() {
    const stack = this.pointUndo.get(String(this.imageIdValue))
    const id = stack?.pop()
    if (!id) return this.flashPointStatus("Nothing to undo on this image")
    this.deleteAnnotation(id)
  }

  deleteSelectedPoint() {
    const g = this.selectedBoxElement()
    if (!g || g.dataset.point !== "true") return false
    const id = g.dataset.annotationId
    const stack = this.pointUndo.get(String(this.imageIdValue))
    if (stack) this.pointUndo.set(String(this.imageIdValue), stack.filter((x) => String(x) !== String(id)))
    this.deleteAnnotation(id)
    return true
  }

  deleteAnnotation(id) {
    const imageId = this.imageIdValue
    fetch(`/images/${imageId}/image_annotations/${id}`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "Accept": "text/vnd.turbo-stream.html", "X-CSRF-Token": this.csrfToken() }
    })
      .then((response) => response.text())
      .then((html) => {
        if (this.selectedId === String(id)) this.deselect()
        window.Turbo.renderStreamMessage(html)
        requestAnimationFrame(() => {
          this.syncEmptyState()
          this.signalAnnotationChanged()
        })
      })
  }

  flashPointStatus(message) {
    if (!this.hasPointStatusTarget) return
    this.pointStatusTarget.textContent = message
    clearTimeout(this._pointFlash)
    this._pointFlash = setTimeout(() => this.updatePointStatus(), 1800)
  }

  stepImage(direction) {
    const gallery = this.galleryController()
    if (!gallery) return
    direction > 0 ? gallery.nextImage() : gallery.prevImage()
  }

  isTypingTarget(target) {
    return !!target?.closest?.("input, textarea, select, [contenteditable='true']")
  }

  // Document keydown for Point mode. P toggles it from anywhere on the Annotate
  // pane; everything else only listens while the mode is on.
  handlePointKey(event) {
    if (!this.isAnnotatePaneActive() || this.isTypingTarget(event.target)) return
    if (event.altKey || event.metaKey) return
    const key = event.key

    if (!event.ctrlKey && (key === "p" || key === "P")) {
      event.preventDefault()
      return this.togglePointMode()
    }
    if (!this.pointMode) return

    if (key === "ArrowRight" || key === ".") { event.preventDefault(); return this.stepImage(1) }
    if (key === "ArrowLeft" || key === ",") { event.preventDefault(); return this.stepImage(-1) }
    if (key === "z" || key === "Z") { event.preventDefault(); return this.undoPoint() }
    if (event.ctrlKey) return

    if (key === "Delete" || key === "Backspace") {
      if (this.deleteSelectedPoint()) event.preventDefault()
      return
    }
    if (key === "Escape") {
      // The deselect handler runs first on Escape; only leave the mode when
      // nothing is selected (so Esc clears a selection before ending the mode).
      if (!this.selectedId) this.disablePointMode()
      return
    }
    if (/^[0-9]$/.test(key)) {
      const chip = this.pointChipTargets[key === "0" ? 9 : Number(key) - 1]
      if (chip) { event.preventDefault(); this.setPointLabel(chip.dataset.label) }
    }
  }

  // One point on the labels layer: a counter-scaled dot (stays ~14px on screen at
  // any zoom, upright) plus a compact class label beside it. The dot is the click /
  // right-click target for selecting or deleting the point; pointer-events are on
  // for the marker only, the rest of the layer stays click-through. Pure DOM.
  appendPointMarker(layer, { g, x, y, layerW, layerH, colour, selected, text, id, s, counterRotate }) {
    const fill = selected ? "#dc2626" : colour

    const dot = this.pointDotElement({ x, y, fill, layerW, layerH, s, counterRotate })
    dot.dataset.annotationMarker = id
    dot.dataset.annotationId = id
    dot.style.pointerEvents = "auto"
    dot.style.cursor = "pointer"
    dot.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.selectAnnotation(id)
    })
    dot.addEventListener("contextmenu", (event) => this.openBoxMenu(event))
    layer.appendChild(dot)

    // The class label is hidden by default (the dot colour carries the class) and
    // shows on hover, or while the point is selected.
    const tag = document.createElement("div")
    tag.dataset.annotationLabel = id
    tag.style.position = "absolute"
    tag.style.left = `${x * layerW}px`
    tag.style.top = `${y * layerH}px`
    tag.style.transformOrigin = "left center"
    tag.style.transform = `scale(${1 / s}) rotate(${counterRotate}deg) translate(11px, -50%)`
    tag.style.whiteSpace = "nowrap"
    tag.style.padding = "0 4px"
    tag.style.borderRadius = "3px"
    tag.style.fontSize = "10px"
    tag.style.lineHeight = "1.4"
    tag.style.fontWeight = "600"
    tag.style.textTransform = "capitalize"
    tag.style.color = "#ffffff"
    tag.style.background = fill
    tag.style.opacity = "0.9"
    tag.style.pointerEvents = "none"
    tag.style.display = selected ? "block" : "none"
    tag.textContent = text
    dot.addEventListener("mouseenter", () => { tag.style.display = "block" })
    dot.addEventListener("mouseleave", () => { tag.style.display = selected ? "block" : "none" })
    layer.appendChild(tag)
  }

  // The round marker itself (counter-scaled so it stays ~14px and upright at any
  // zoom). Shared by saved points and the optimistic placeholder.
  pointDotElement({ x, y, fill, layerW, layerH, s, counterRotate }) {
    const dot = document.createElement("div")
    dot.style.position = "absolute"
    dot.style.left = `${x * layerW}px`
    dot.style.top = `${y * layerH}px`
    dot.style.width = "14px"
    dot.style.height = "14px"
    dot.style.borderRadius = "9999px"
    dot.style.background = fill
    dot.style.border = "2px solid #ffffff"
    dot.style.boxShadow = "0 0 0 1px rgba(0,0,0,0.45), 0 1px 3px rgba(0,0,0,0.5)"
    dot.style.transformOrigin = "center center"
    dot.style.transform = `translate(-50%, -50%) scale(${1 / s}) rotate(${counterRotate}deg)`
    return dot
  }

  // --- helpers ----------------------------------------------------------

  clamp(value, min, max) {
    return Math.max(min, Math.min(max, value))
  }

  round(value) {
    return Math.round(value * 10000) / 10000
  }

  isAnnotatePaneActive() {
    // Read the pane-selector's data attribute directly — Stimulus keeps it in sync
    // with rightActiveValue and it's available as soon as the HTML is parsed, with
    // no dependency on controller connect timing.
    const attrValue = this.element.dataset.paneSelectorRightActiveValue
    if (attrValue !== undefined) return attrValue === 'annotate'
    // Fallback for headless tests or delayed connect.
    const pane = this.element.querySelector('[data-content-type="annotate"]')
    return !!pane && !pane.classList.contains("hidden")
  }
}
